/**
 * Localized Info help catalog.
 *
 * Keys are the `title` (or `infoKey`) passed to <InfoButton> / PageHeading `infoTitle`.
 * Each entry holds `en` and `ko` copy: `{ title, paragraphs: string[] }`.
 * `**bold**` inside a paragraph is rendered by <InfoParagraphs>.
 *
 * When a key is missing (dynamic titles such as a building name, SS code or DB table),
 * `lookupInfo` returns null and <InfoButton> falls back to its English children.
 */

const CATALOG = {
  /* ---------------------------------------------------------------- Home */

  Dashboard: {
    en: {
      title: 'Dashboard',
      paragraphs: [
        'The dashboard is the daily start page. It answers one question: **what needs an admin action today?**',
        '**Due queue** is the single source of truth for work that is late or coming up: **Over Due** (past the due date) plus **Urgent** (still due within 30 days). The same rows appear in the Due queue panel, the To-Do popup and the Inspections list, so the counts always agree.',
        'Rows can exist **before** a work pack is issued. The normal path is: due date approaches → the system asks the default IQP or partner company for a visit date → the pack is issued automatically. Issue manually only when that did not happen.',
        'From here you can search, select rows, bulk **Schedule**, send an **Urgent** email digest, or force-move an item into another queue. Open a row to reach the building or the inspection detail.',
      ],
    },
    ko: {
      title: '대시보드',
      paragraphs: [
        '대시보드는 하루를 시작하는 화면입니다. 답하는 질문은 하나입니다. **오늘 관리자가 처리할 일이 무엇인가?**',
        '**Due queue**는 늦었거나 곧 닥칠 작업의 유일한 기준입니다. **Over Due**(만기일 경과) + **Urgent**(30일 내 만기)로 구성됩니다. Due queue 패널, To-Do 팝업, Inspections 목록이 모두 같은 행을 쓰기 때문에 숫자가 항상 일치합니다.',
        '작업 팩(work pack)이 발행되기 **전**에도 행이 나타날 수 있습니다. 정상 흐름은 만기일 접근 → 시스템이 기본 IQP 또는 파트너 회사에 방문 날짜 요청 → 날짜가 오면 팩 자동 발행입니다. 수동 발행은 그 과정이 실패했을 때만 씁니다.',
        '여기서 검색·선택 후 일괄 **Schedule**, **Urgent** 메일 다이제스트 발송, 다른 큐로 강제 이동을 할 수 있습니다. 행을 열면 건물 또는 인스펙션 상세로 이동합니다.',
      ],
    },
  },

  /* ----------------------------------------------------------- Portfolio */

  Buildings: {
    en: {
      title: 'Buildings',
      paragraphs: [
        'The building portfolio. Each row is one building with one Compliance Schedule (CS) and one BWOF cycle.',
        'The Line-2 tabs split the portfolio by service model: **All**, **Full agent** (we run the whole BWOF), **Partly SS only** (we handle selected Specified Systems), **New building**, and **Inactived list**.',
        'Buildings are **never hard-deleted** from the UI. Delete on a row is a two-step confirm and sets the building to **INACTIVE** — all data is kept and can be restored from the Inactived list.',
        '**Add / Edit Building** captures name and address only. Parts, levels and drawings live under **Building structure & drawings**; SS unit placement lives under **Floor plan**.',
      ],
    },
    ko: {
      title: '건물 목록',
      paragraphs: [
        '건물 포트폴리오입니다. 한 행 = 하나의 건물이며, Compliance Schedule(CS) 1건과 BWOF 사이클 1건에 대응합니다.',
        '두 번째 줄 탭은 서비스 형태로 포트폴리오를 나눕니다. **All**, **Full agent**(BWOF 전체 대행), **Partly SS only**(지정한 SS만 담당), **New building**, **Inactived list**.',
        '건물은 UI에서 **완전 삭제되지 않습니다**. 행의 Delete는 2단계 확인을 거쳐 **INACTIVE**로 바꿉니다. 데이터는 모두 남고 Inactived list에서 복원할 수 있습니다.',
        '**Add / Edit Building**에서는 이름과 주소만 입력합니다. 동·층·도면은 **Building structure & drawings**, SS 유닛 배치는 **Floor plan**에서 관리합니다.',
      ],
    },
  },

  'Inactived list': {
    en: {
      title: 'Inactived list',
      paragraphs: [
        'This is **not a trash bin**. Buildings here still hold every record: Compliance Schedule text, SS instances, units, inspections, paperwork and documents.',
        'A building lands here when someone confirms Delete on the portfolio list. That sets `status = INACTIVE`; nothing is removed from the database.',
        'Use it for buildings you no longer service (contract ended, demolished, handed to another agent) so they stop appearing in Due queue, Schedule and worker app packs.',
        '**Restore** puts the building back into the active portfolio with all history intact.',
      ],
    },
    ko: {
      title: '비활성 건물 목록',
      paragraphs: [
        '이 화면은 **휴지통이 아닙니다**. 여기 있는 건물도 Compliance Schedule 본문, SS 인스턴스, 유닛, 인스펙션, 서류, 문서를 모두 그대로 보관합니다.',
        '포트폴리오 목록에서 Delete를 확인하면 이 목록으로 옮겨집니다. `status = INACTIVE`로만 바뀌고 DB에서 지워지지 않습니다.',
        '계약 종료·철거·타 대행사 이관 등으로 더 이상 관리하지 않는 건물에 사용하세요. Due queue, Schedule, 작업자 앱 팩에서 빠집니다.',
        '**Restore**를 누르면 이력 그대로 활성 포트폴리오로 돌아옵니다.',
      ],
    },
  },

  Building: {
    en: {
      title: 'Building',
      paragraphs: [
        'The building hub. The header keeps one compact row — **Back · Title · Info · actions** — and every building shares the same Line-2 tabs: **Overview · SS Units**, then any inspection module that applies to this building (**Chiller**, **Boiler**, …), then **Floor plan · Inspections · Paperwork · Documents · Checklists**.',
        '**Overview** shows building basics, BWOF due date and CS anniversary, BWOF progress, service mandates, and the **Compliance Schedule – Specified Systems** list (`SS {code} {location}`, sorted by SS number). **Open CS** goes to that SS hub.',
        '**Inspection scope** can be FULL or PARTIAL. On PARTIAL only the SS marked *include in inspection* appear on operational pages (BWOF progress, Order, Units, Floor plan, Paperwork, Checklists, jobs, worker packs).',
        'Data sources are deliberate: the SS catalogue comes from `building_ss_instances`, devices from `ss_units`, subtypes from `ss_instance_equipment_types`, and checklist templates from `checklist_sets`.',
      ],
    },
    ko: {
      title: '건물 상세',
      paragraphs: [
        '건물 허브입니다. 헤더는 **Back · 제목 · Info · 액션** 한 줄로 통일되어 있고, 모든 건물이 같은 두 번째 줄 탭을 씁니다. **Overview · SS Units** 다음에, 이 건물에 적용된 인스펙션 모듈(**Chiller**, **Boiler**, …)이 순서대로 오고, 그 다음 **Floor plan · Inspections · Paperwork · Documents · Checklists**입니다.',
        '**Overview**에는 건물 기본정보, BWOF 만기일과 CS 기념일, BWOF 진행률, 서비스 위임(mandate), 그리고 **Compliance Schedule – Specified Systems** 목록(`SS {코드} {위치}`, SS 번호순)이 있습니다. **Open CS**로 해당 SS 허브로 이동합니다.',
        '**Inspection scope**는 FULL 또는 PARTIAL입니다. PARTIAL이면 *include in inspection*으로 표시된 SS만 운영 화면(BWOF progress, Order, Units, Floor plan, Paperwork, Checklists, jobs, 작업자 팩)에 나옵니다.',
        '데이터 출처는 의도적으로 구분됩니다. SS 카탈로그는 `building_ss_instances`, 장치·유닛은 `ss_units`, 서브타입은 `ss_instance_equipment_types`, 체크리스트 템플릿은 `checklist_sets`입니다.',
      ],
    },
  },

  'Floor plan': {
    en: {
      title: 'Floor plan',
      paragraphs: [
        'Floor plan is for **placing SS units on a drawing** — it is not where drawings are uploaded. Pick a **Place** (building part + level sheet), choose the SS and its registered subtype, then click the drawing to create the next numbered unit.',
        'Unit IDs are always `{prefix}-{n}` (for example `1.1-1-1`). There is no bare-prefix unit. Deleting a unit soft-deletes it and frees the number, so the next placement reuses the lowest free gap. Duplicate IDs are blocked with a warning.',
        'Left-click a marker to open the equipment + checklist modal, right-click to delete, drag then **Save** to move. Markers are anchored to the drawing image, not the image box, so they stay aligned when the viewport changes.',
        'Drawings themselves are uploaded in **Edit Building → Building structure & drawings** (per part, per level; PDF crop supported). Zoom uses the +/− buttons only; the wheel scrolls the page.',
        'The right panel (**Change SS unit ID**) lists units for the selected SS — including inactive ones so taken numbers are visible — and lets you edit the suffix or drag rows to renumber `prefix-1…n`.',
      ],
    },
    ko: {
      title: '평면도',
      paragraphs: [
        'Floor plan은 **도면 위에 SS 유닛을 배치**하는 화면입니다. 도면 업로드 화면이 아닙니다. **Place**(동 + 층 시트)를 고르고 SS와 등록된 서브타입을 선택한 뒤 도면을 클릭하면 다음 번호 유닛이 생성됩니다.',
        '유닛 ID는 항상 `{prefix}-{n}` 형식입니다(예: `1.1-1-1`). prefix만 있는 유닛은 없습니다. 유닛 삭제는 소프트 삭제로 번호를 반납하므로, 다음 배치는 가장 낮은 빈 번호를 씁니다. 중복 ID는 경고와 함께 저장이 막힙니다.',
        '마커 좌클릭 = 장비·체크리스트 모달, 우클릭 = 삭제, 드래그 후 **Save** = 위치 이동입니다. 마커는 이미지 박스가 아니라 도면 이미지에 붙기 때문에 화면 크기가 바뀌어도 위치가 유지됩니다.',
        '도면 자체는 **Edit Building → Building structure & drawings**에서 동별·층별로 업로드합니다(PDF 크롭 지원). 확대·축소는 +/− 버튼만 사용하고, 마우스 휠은 페이지를 스크롤합니다.',
        '오른쪽 패널(**Change SS unit ID**)은 선택한 SS의 유닛을 보여 줍니다. 사용 중인 번호를 확인할 수 있도록 비활성 유닛도 표시하며, 대시 뒤 번호를 수정하거나 행을 드래그해 `prefix-1…n`으로 재번호할 수 있습니다.',
      ],
    },
  },

  'Unit hub': {
    en: {
      title: 'Unit hub',
      paragraphs: [
        'One unit = one physical device or zone under a Specified System. The hub is the operational source of truth for it.',
        'It shows identity (unique ID, name, QR, manufacturer / model / serial), location and level, inspection **frequency**, the assigned inspector or partner, the **confirmed checklist** for this unit, and site photos.',
        '**Set Inspection List** is the only place to edit this unit’s questions. Questions are picked from the SS default pool or the equipment-type pool and then snapshotted onto the unit, so later template changes do not silently rewrite past work.',
        'Operational fields live on `ss_units`; `building_ss_instances` only supplies defaults for **new** units. Editing the instance cascades defaults down to child units.',
        '**Delete** removes the unit from operational lists (status INACTIVE) and frees its ID number for reuse.',
      ],
    },
    ko: {
      title: '유닛 허브',
      paragraphs: [
        '유닛 1개 = Specified System에 속한 실제 장치 또는 구역 1개입니다. 이 허브가 그 유닛의 운영 기준 화면입니다.',
        '식별 정보(고유 ID, 이름, QR, 제조사·모델·시리얼), 위치와 층, 점검 **주기(frequency)**, 담당 검사자 또는 파트너, 이 유닛의 **확정 체크리스트**, 현장 사진을 보여 줍니다.',
        '**Set Inspection List**가 이 유닛의 문항을 편집하는 유일한 경로입니다. SS 기본 풀 또는 장비 타입 풀에서 문항을 골라 유닛에 스냅샷으로 저장하므로, 이후 템플릿이 바뀌어도 과거 작업이 조용히 덮이지 않습니다.',
        '운영 필드는 `ss_units`에 있습니다. `building_ss_instances`는 **새** 유닛의 기본값만 제공하며, 인스턴스를 수정하면 하위 유닛으로 기본값이 전파됩니다.',
        '**Delete**는 유닛을 운영 목록에서 제거(INACTIVE)하고 ID 번호를 재사용할 수 있게 반납합니다.',
      ],
    },
  },

  'SS Units': {
    en: {
      title: 'SS Units',
      paragraphs: [
        'The device register for this building: every unit under every in-scope Specified System, grouped by SS and subtype.',
        '**Subtypes** come from a fixed catalogue (`ss_instance_equipment_types`) with stable numbers — **Default is always `1`** (`1.1-1`), Wet = `2`, Dry = `3`, and unused numbers are skipped. Units of the same subtype share one question snapshot.',
        'The subtype wizard can add a **custom** subtype for a customer-specific arrangement. **Delete** on a custom subtype soft-deletes its units and then removes the subtype (catalogue subtypes are kept).',
        'This list shows operational units only (status ACTIVE / REPAIRING / DRAFT), read through one shared service so the web list, the worker building pack and app inspection all see exactly the same set.',
        'Click a row to open the **Unit hub**. Add unit also lands on the hub so identity and checklist can be finished in one place.',
      ],
    },
    ko: {
      title: 'SS 유닛',
      paragraphs: [
        '이 건물의 장치 등록부입니다. 검사 범위에 포함된 모든 Specified System의 유닛을 SS·서브타입별로 묶어 보여 줍니다.',
        '**서브타입**은 고정 카탈로그(`ss_instance_equipment_types`)에서 오며 번호가 고정입니다. **Default는 항상 `1`**(`1.1-1`), Wet=`2`, Dry=`3`이고 미사용 번호는 건너뜁니다. 같은 서브타입 유닛은 질문 스냅샷을 공유합니다.',
        '서브타입 마법사에서 고객 특수 구성을 위한 **커스텀** 서브타입을 추가할 수 있습니다. 커스텀 서브타입의 **Delete**는 해당 유닛을 소프트 삭제한 뒤 서브타입을 제거합니다(카탈로그 서브타입은 유지).',
        '이 목록에는 운영 유닛(ACTIVE / REPAIRING / DRAFT)만 나옵니다. 하나의 공용 서비스로 읽기 때문에 웹 목록, 작업자 건물 팩, 앱 점검이 완전히 같은 집합을 봅니다.',
        '행을 클릭하면 **Unit hub**로 이동합니다. Add unit도 허브로 이동해 식별 정보와 체크리스트를 한곳에서 마무리합니다.',
      ],
    },
  },

  Checklists: {
    en: {
      title: 'Checklists',
      paragraphs: [
        'The checklist sets used for this building, listed by SS in SS-number order (SS 1 → 2 → 3 → 3.1 → 4 → 10 → 15.2, not string order).',
        'Content starts from the **Unit Standard pool**: mandatory items are included by default, optional items can be added from the pool, unnecessary items removed, and custom items written by hand.',
        'Each item carries Category / Inspection item / Method / Standard / Measured value / Unit / P–F. Items with `value_type` and `pass_threshold` are judged automatically from the measured value.',
        'Each question can also carry long how-to text (**Info**) and optional guidance photos. Those flow pool → unit confirmed snapshot → worker app, so the field worker sees the same guidance above the pass criteria.',
        'This page is about **templates**. Per-unit questions are confirmed on the Unit hub via Set Inspection List.',
      ],
    },
    ko: {
      title: '체크리스트',
      paragraphs: [
        '이 건물에 쓰이는 체크리스트 세트입니다. SS 번호순으로 정렬됩니다(문자열 정렬이 아니라 SS 1 → 2 → 3 → 3.1 → 4 → 10 → 15.2).',
        '내용은 **Unit Standard 풀**에서 시작합니다. 필수 항목은 기본 포함, 필요한 항목은 풀에서 추가, 불필요한 항목은 제거, 특수 항목은 커스텀으로 직접 작성합니다.',
        '각 항목은 Category / Inspection item / Method / Standard / Measured value / Unit / P–F를 가집니다. `value_type`과 `pass_threshold`가 있는 항목은 측정값으로 자동 판정됩니다.',
        '문항마다 상세 설명(**Info**)과 안내 사진을 붙일 수 있습니다. 이 내용은 풀 → 유닛 확정 스냅샷 → 작업자 앱으로 전달되어, 현장 작업자가 합격 기준 위에서 같은 안내를 봅니다.',
        '이 화면은 **템플릿**을 다룹니다. 유닛별 문항 확정은 Unit hub의 Set Inspection List에서 합니다.',
      ],
    },
  },

  'SS paperwork': {
    en: {
      title: 'SS paperwork',
      paragraphs: [
        'Certificates and reports issued per Specified System for this building, with the short certificate type shown on each row (12A / S-RaD / Defect).',
        '**Form 12A** is the short legal certificate (Building Act s108(3)(c) and the Form 12A regulations) and requires IMR plus all units passing. The **Inspection test report** is the detailed per-unit checklist, filed in the same `Form-12A/{date}/` folder.',
        'The worker app issues these: SS Done → preview → IQP signature → issue. After issue the SS is **locked** in the app so the same certificate cannot be produced twice.',
        '**Amend** reopens it from the office: set unit pass/fail, IMR, draw the IQP signature, enter the worker password — the old documents are superseded and new ones re-issued.',
        'Certificate copies are filed under `{BuildingName}/{YYYY}/` as `ss {code}-{12a|srad|brad|defect}-{BuildingName}-{YYYY-MM-DD}`.',
      ],
    },
    ko: {
      title: 'SS 서류',
      paragraphs: [
        '이 건물의 Specified System별 발행 증명서·리포트 목록입니다. 각 행에 증명서 종류가 짧게 표시됩니다(12A / S-RaD / Defect).',
        '**Form 12A**는 간단한 법적 증명서(Building Act s108(3)(c), Form 12A 규정)로 IMR과 전 유닛 합격이 필요합니다. **Inspection test report**는 유닛별 상세 체크리스트이며 같은 `Form-12A/{날짜}/` 폴더에 저장됩니다.',
        '발행은 작업자 앱에서 합니다. SS Done → 미리보기 → IQP 서명 → 발행. 발행 후에는 앱에서 해당 SS가 **잠기므로** 같은 증명서를 두 번 만들 수 없습니다.',
        '**Amend**로 사무실에서 다시 열 수 있습니다. 유닛 pass/fail, IMR, IQP 서명 캔버스, 작업자 비밀번호를 입력하면 기존 문서를 대체(supersede)하고 새로 발행합니다.',
        '증명서 사본은 건물 문서 캐비닛에 `{건물명}/{연도}/ss 코드-12a|srad|brad|defect-건물명-발행일` 형식으로 저장됩니다.',
      ],
    },
  },

  'Chiller / plant': {
    en: {
      title: 'Chiller / plant',
      paragraphs: [
        'An **optional plant module**, separate from the BWOF product. It tracks chillers and similar plant that need periodic inspection or calibration but are not Specified Systems.',
        'The tab only appears when the module is enabled for the company in Settings (`chiller_module_enabled`).',
        'Add plant assets for this building with their own due dates. Records live in the inspection module tables, not in the BWOF SS tables.',
        'Temperature calibration compares each probe with a reference thermometer. A chiller usually has one door controller (compressor on/off) plus one or more monitoring probes — large cool stores often list several monitoring cards on the same walk-in. Read the door controller at the room. For each monitoring probe, note the reference temperature and the minute, then read that same minute on the logger (often 30–50 m away). Pass when the absolute difference is within that sensor’s limit (NTC/PTC default 1.0°C, PT100/PT1000 default 0.5°C). Limits and equipment are set per sensor on the asset. One building reference meter is reused until replaced. The phone shows every saved sensor card. Change the meter or room equipment on the web.',
        'Use the main-menu **Chiller** page for the cross-portfolio queue.',
      ],
    },
    ko: {
      title: '칠러 / 플랜트',
      paragraphs: [
        'BWOF 제품과 별개인 **선택 모듈**입니다. Specified System은 아니지만 주기 점검·교정이 필요한 칠러 등 설비를 관리합니다.',
        'Settings에서 회사 단위로 모듈을 켠 경우에만 탭이 나타납니다(`chiller_module_enabled`).',
        '이 건물의 설비 자산과 만기일을 등록합니다. 데이터는 인스펙션 모듈 테이블에 저장되며 BWOF SS 테이블과 분리되어 있습니다.',
        '온도 캘리브레이션은 센서마다 기준 온도계와 비교합니다. 문 앞 조절기는 그 자리에서 읽고, 종합 컨트롤러는 보통 30–50m 떨어져 있으므로 기준 온도와 시각(분)을 적고 그 분의 로그 값을 읽습니다. 차이의 절대값이 한도 안이면 합격입니다. NTC/PTC 기본 한도는 1.0°C, PT100/PT1000 기본 한도는 0.5°C이며 한도는 바꿀 수 있습니다. 컨트롤러 모델, 센서 종류, 종합기 모델, 종합기 센서, 합격 한도는 자산에 저장합니다. 기준 온도계의 회사·모델·시리얼은 건물마다 한 번 저장하고 교체 전까지 다시 씁니다. 측정자 이름은 로그인한 사람이 기본이고, 폰은 그 설정을 보여 주며 온도 두 쌍만 받습니다. 기준 온도계와 기기 설정은 웹에서 고칩니다.',
        '포트폴리오 전체 큐는 메인 메뉴의 **Chiller** 화면에서 봅니다.',
      ],
    },
  },

  'Chiller / plant queue': {
    en: {
      title: 'Chiller / plant queue',
      paragraphs: [
        'The portfolio-wide plant queue: every chiller or plant asset across all buildings, ordered by its next due date.',
        'This is the non-BWOF work stream. It is intentionally kept out of Due queue and BWOF progress so certificate compliance is never mixed with maintenance jobs.',
        'Open a row to reach the building’s Chiller tab, where assets and due dates are edited.',
        'Inspection and calibration workflow (and the app side) are the next phase of this module.',
      ],
    },
    ko: {
      title: '칠러 / 플랜트 큐',
      paragraphs: [
        '포트폴리오 전체 설비 큐입니다. 모든 건물의 칠러·플랜트 자산을 다음 만기일 순으로 보여 줍니다.',
        'BWOF가 아닌 별도 작업 흐름입니다. 증명서 컴플라이언스와 유지보수 작업이 섞이지 않도록 Due queue와 BWOF progress에서 의도적으로 분리했습니다.',
        '행을 열면 해당 건물의 Chiller 탭으로 이동해 자산과 만기일을 수정할 수 있습니다.',
        '점검·교정 워크플로와 앱 연동은 이 모듈의 다음 단계입니다.',
      ],
    },
  },

  Schedule: {
    en: {
      title: 'Schedule',
      paragraphs: [
        'The calendar view of inspection work: confirmed visit dates, notify events and due windows.',
        '**Sync & notify due** runs the inspection lead-time cycle. It creates NOTIFIED records and emails the IQP and the partner company together, then shows Notify chips on the calendar.',
        'Lead times follow the frequency: MONTHLY = 2 weeks before due (visit remind 1 week), QUARTERLY and SEMI-ANNUAL = 1 month before (remind 2 weeks), ANNUAL = 2 months before (remind 1 month).',
        'When the assignee confirms a date the item becomes **Scheduled**; on the day it moves to **In progress**; results make it **Completed** (pass) or **Failed → Repairing**.',
        'Calendar and To-Do preferences are saved per user on the server, so the same view follows you across browsers and devices.',
      ],
    },
    ko: {
      title: '일정',
      paragraphs: [
        '인스펙션 작업의 캘린더 화면입니다. 확정된 방문일, 통지(notify) 이벤트, 만기 구간을 보여 줍니다.',
        '**Sync & notify due**는 리드타임 사이클을 실행합니다. NOTIFIED 레코드를 만들고 IQP와 파트너 회사에 함께 메일을 보낸 뒤 캘린더에 Notify 칩을 표시합니다.',
        '리드타임은 주기에 따릅니다. MONTHLY = 만기 2주 전(방문 리마인드 1주), QUARTERLY·SEMI-ANNUAL = 1개월 전(리마인드 2주), ANNUAL = 2개월 전(리마인드 1개월).',
        '담당자가 날짜를 확정하면 **Scheduled**, 방문일에 **In progress**, 결과가 오면 **Completed**(합격) 또는 **Failed → Repairing**이 됩니다.',
        '캘린더와 To-Do 설정은 사용자별로 서버에 저장되므로 다른 브라우저·기기에서도 같은 화면이 유지됩니다.',
      ],
    },
  },

  Repairing: {
    en: {
      title: 'Repairing',
      paragraphs: [
        'The repair track. Every row here is an inspection that **failed** and is now being quoted, repaired and re-inspected.',
        'It is the *same* inspection record, not a new one — building + SS code + due date + status, with status REPAIRING. That keeps the history of the failure and the fix on one item.',
        'Admin path: open the inspection and finish it. Complete after the repair passes re-inspection; the row then leaves this list.',
        'Result colours are consistent everywhere: **Pass = green, Incomplete = yellow, Fail = red**.',
      ],
    },
    ko: {
      title: '수리 진행',
      paragraphs: [
        '수리 트랙입니다. 여기 있는 각 행은 **불합격(FAIL)**된 인스펙션이며 현재 견적·수리·재검사 단계에 있습니다.',
        '새 레코드가 아니라 *같은* 인스펙션 레코드입니다. 건물 + SS 코드 + 만기일 + 상태 구조에서 상태만 REPAIRING입니다. 그래서 불합격과 조치 이력이 한 항목에 남습니다.',
        '관리자 처리: 인스펙션을 열어 마무리합니다. 재검사 합격 후 Complete하면 이 목록에서 사라집니다.',
        '결과 색상은 전 화면 공통입니다. **Pass = 초록, Incomplete = 노랑, Fail = 빨강**.',
      ],
    },
  },

  'Repairing = failed SS inspection track': {
    en: {
      title: 'Repairing = failed SS inspection track',
      paragraphs: [
        'Repairing is **not a separate job type**. After a FAIL the same inspection record continues as the repair track for that Specified System.',
        'Sequence: inspection FAILED → defect form → quote → repair → re-inspect → Pass → Completed. Only the status changes; building, SS code and due date stay.',
        'This matters for reporting: a building’s BWOF cycle cannot close while any SS sits in Repairing, because the council pack needs all SS passing.',
        'From a row, use **Open inspection & finish** to record the outcome. Do not issue a new inspection for the same SS just to log the repair.',
      ],
    },
    ko: {
      title: 'Repairing = 불합격 SS 인스펙션 트랙',
      paragraphs: [
        'Repairing은 **별도 작업 유형이 아닙니다**. FAIL 이후 같은 인스펙션 레코드가 그 Specified System의 수리 트랙으로 이어집니다.',
        '순서: 인스펙션 FAILED → 하자(defect) 양식 → 견적 → 수리 → 재검사 → Pass → Completed. 상태만 바뀌고 건물·SS 코드·만기일은 그대로입니다.',
        '보고 관점에서 중요합니다. 시청 제출 묶음은 모든 SS 합격을 요구하므로, SS 하나라도 Repairing이면 그 건물의 BWOF 사이클을 닫을 수 없습니다.',
        '행에서 **Open inspection & finish**로 결과를 기록하세요. 수리 기록만을 위해 같은 SS에 새 인스펙션을 발행하지 마세요.',
      ],
    },
  },

  /* -------------------------------------------------------------- People */

  Staff: {
    en: {
      title: 'Staff',
      paragraphs: [
        'One directory for **every login** — office admins and field workers together.',
        '**Username** is the login ID for both app and web, and the password is shared between them. Add/Edit uses one profile form (name, email, phone, address, licence / IQP number, access group).',
        '**Login type** is Worker (app / field) or Admin (office web); the profile fields are identical. **Access group** is Admin / Worker internal / Worker external / Guest. Setting the group to Admin makes that account log in as an admin — the person must re-login for it to take effect.',
        '**Rights** are per person: Read / Edit / Delete for office work plus Setup / Lock for unit setup. Assigning Admin grants all five by default so authority can be handed over completely.',
        '**Active** controls login: inactive accounts cannot sign in. **IQP / SS** grades apply to worker accounts and can be edited inline or from the row button.',
      ],
    },
    ko: {
      title: '직원',
      paragraphs: [
        '**모든 로그인 계정**을 한곳에서 관리하는 디렉터리입니다. 사무실 관리자와 현장 작업자를 함께 다룹니다.',
        '**Username**은 앱과 웹 공통 로그인 ID이고 비밀번호도 공유합니다. 추가·수정은 하나의 프로필 폼(이름, 이메일, 전화, 주소, 라이선스/IQP 번호, 접근 그룹)을 사용합니다.',
        '**Login type**은 Worker(앱·현장) 또는 Admin(사무실 웹)이며 프로필 항목은 동일합니다. **Access group**은 Admin / Worker internal / Worker external / Guest입니다. 그룹을 Admin으로 바꾸면 관리자로 로그인되지만, 반영되려면 다시 로그인해야 합니다.',
        '**권한(Rights)**은 개인별입니다. 사무실 업무용 Read / Edit / Delete와 유닛 설정용 Setup / Lock. Admin을 지정하면 5개가 모두 기본 부여되어 권한을 온전히 이전할 수 있습니다.',
        '**Active**는 로그인 가능 여부입니다. 비활성 계정은 로그인할 수 없습니다. **IQP / SS** 등급은 작업자 계정에 적용되며 Edit 인라인 또는 행 버튼에서 수정합니다.',
      ],
    },
  },

  Workers: {
    en: {
      title: 'Workers',
      paragraphs: [
        'Field workers (IQPs) with their SS grades, assigned buildings and app link status. Kept alongside Staff because IQP detail is worker-specific.',
        'The card light shows app connectivity: **green** = app online (recent heartbeat), **yellow** = ready / idle, **red** = inactive.',
        'A worker’s app portfolio is: default buildings + admin assignments + buildings that appear on their jobs. Master / Admin accounts download every building.',
        'SS grades decide which Specified Systems that worker may inspect, and they drive the default-inspector suggestions on building SS instances.',
      ],
    },
    ko: {
      title: '작업자',
      paragraphs: [
        '현장 작업자(IQP) 목록입니다. SS 등급, 배정 건물, 앱 연결 상태를 보여 줍니다. IQP 상세는 작업자 고유 정보라 Staff와 별도로 유지합니다.',
        '카드의 표시등은 앱 연결 상태입니다. **초록** = 앱 온라인(최근 heartbeat), **노랑** = 준비/유휴, **빨강** = 비활성.',
        '작업자의 앱 포트폴리오는 기본 건물 + 관리자 배정 + 본인 작업에 나온 건물입니다. Master / Admin 계정은 모든 건물을 내려받습니다.',
        'SS 등급은 그 작업자가 점검할 수 있는 Specified System을 결정하며, 건물 SS 인스턴스의 기본 검사자 추천에도 사용됩니다.',
      ],
    },
  },

  Worker: {
    en: {
      title: 'Worker',
      paragraphs: [
        'One worker profile. Name, contact, IQP / licence number, access group, active flag and **SS grades**.',
        'Worker ID is generated automatically. IQP registrations list immediately as you enter them and are filtered by SS code, so the grades stay consistent with the SS catalogue.',
        'The login here is the app login. The same username and password work on the office web if the access group allows it.',
        'Assign buildings so the worker’s app pack downloads the right portfolio. Assignment also decides whether that worker may move floor-plan markers on a building, when the building allows marker moves.',
      ],
    },
    ko: {
      title: '작업자 상세',
      paragraphs: [
        '작업자 1명의 프로필입니다. 이름, 연락처, IQP·라이선스 번호, 접근 그룹, 활성 여부, **SS 등급**.',
        'Worker ID는 자동 생성됩니다. IQP 등록은 입력하는 즉시 목록에 나타나고 SS 코드로 필터링되어 SS 카탈로그와 등급이 어긋나지 않습니다.',
        '여기의 로그인이 앱 로그인입니다. 접근 그룹이 허용하면 같은 아이디·비밀번호로 사무실 웹에도 로그인합니다.',
        '건물을 배정하면 작업자 앱 팩이 올바른 포트폴리오를 내려받습니다. 배정은 건물이 마커 이동을 허용할 때 그 작업자가 평면도 마커를 옮길 수 있는지도 결정합니다.',
      ],
    },
  },

  Admins: {
    en: {
      title: 'Admins',
      paragraphs: [
        'Legacy office-admin list. It still works, but new accounts should be created in **Staff**, which covers every login in one place.',
        'Login accepts **username or email**. Office roles are ADMIN / MANAGER / MASTER; MASTER keeps full authority including Compliance Schedule extract and company settings.',
        'A worker account whose access group is Admin also logs in as an admin — that account is managed under Staff, not here.',
        'Deactivate rather than delete so audit history (who scheduled, issued or amended what) stays readable.',
      ],
    },
    ko: {
      title: '관리자',
      paragraphs: [
        '이전 방식의 사무실 관리자 목록입니다. 계속 동작하지만, 새 계정은 모든 로그인을 한곳에서 다루는 **Staff**에서 만드는 것이 좋습니다.',
        '로그인은 **아이디 또는 이메일**을 모두 받습니다. 사무실 역할은 ADMIN / MANAGER / MASTER이며, MASTER는 Compliance Schedule 추출과 회사 설정을 포함한 전체 권한을 가집니다.',
        '접근 그룹이 Admin인 작업자 계정도 관리자로 로그인합니다. 그 계정은 여기가 아니라 Staff에서 관리합니다.',
        '감사 이력(누가 일정을 잡고 발행·수정했는지)이 남도록 삭제보다 비활성화를 사용하세요.',
      ],
    },
  },

  Experts: {
    en: {
      title: 'Experts',
      paragraphs: [
        'The expert pool: specialists who produce the reports an IQP cannot sign off alone — fire, mechanical, electrical, acoustic engineers and similar.',
        'Two views: **All** and **By SS**. By SS groups experts under the SS codes they cover, so you can see who to approach for a given Specified System.',
        'On an Application, the expert modal is filtered by that application’s SS codes and grouped by SS. From there you can send an **Email Query** or do a **Manual Assign** — the same flow either way.',
        'Which expert and which report each SS needs (and why) is defined in the SS expert report guide, shown on the Expert Report stage and next to the AC2109a SS field.',
      ],
    },
    ko: {
      title: '전문가',
      paragraphs: [
        '전문가 풀입니다. IQP 단독으로 확인할 수 없는 리포트를 작성하는 전문가(소방, 기계, 전기, 음향 등)를 관리합니다.',
        '두 가지 보기가 있습니다. **All**과 **By SS**. By SS는 담당 SS 코드별로 전문가를 묶어 보여 주어, 특정 Specified System에 누구를 섭외할지 바로 알 수 있습니다.',
        'Application 화면에서는 그 신청의 SS 코드로 전문가 풀이 필터링되고 SS별로 묶여 표시됩니다. 거기서 **Email Query** 또는 **Manual Assign**을 보낼 수 있으며 이후 흐름은 동일합니다.',
        'SS별로 어떤 전문가와 어떤 리포트가 왜 필요한지는 SS 전문가 리포트 가이드에 정의되어 있고, Expert Report 단계와 AC2109a SS 입력란 옆에 표시됩니다.',
      ],
    },
  },

  Expert: {
    en: {
      title: 'Expert',
      paragraphs: [
        'One expert record: company, discipline, contact details, and the **SS codes** they can report on.',
        'SS codes are what put this expert into the right pool. Keep them accurate — the Application expert modal filters strictly by them.',
        'Note the report types this expert issues (for example fire engineering brief, mechanical performance report, acoustic assessment). That is what the Expert Report stage asks for.',
        'Experts are external parties: they do not get an app login. Contact is by email query or manual assignment recorded on the application.',
      ],
    },
    ko: {
      title: '전문가 상세',
      paragraphs: [
        '전문가 1명(또는 1개 업체)의 레코드입니다. 회사, 분야, 연락처, 그리고 리포트 작성이 가능한 **SS 코드**.',
        'SS 코드가 이 전문가를 어떤 풀에 넣을지 결정합니다. Application 전문가 모달이 이 값으로 엄격하게 필터링하므로 정확히 유지하세요.',
        '이 전문가가 발행하는 리포트 종류(예: 소방 엔지니어링 브리프, 기계 성능 리포트, 음향 평가)를 기록하세요. Expert Report 단계에서 요구하는 항목입니다.',
        '전문가는 외부 당사자이며 앱 로그인을 받지 않습니다. 연락은 이메일 문의 또는 신청서에 기록되는 수동 배정으로 진행합니다.',
      ],
    },
  },

  Partners: {
    en: {
      title: 'Partners',
      paragraphs: [
        'Partner companies that carry out inspections we do not do in-house, plus the documents received from them.',
        'A partner can be the **default assignee** for a building × SS. When the due window opens, the notify cycle emails that company (and the IQP) for a visit date.',
        'Partner contact details feed the SS hub, so the assignee phone and address show next to the Specified System.',
        'Use **Document extraction** to pull structured data out of uploaded partner paperwork instead of retyping it.',
      ],
    },
    ko: {
      title: '파트너',
      paragraphs: [
        '자체 수행하지 않는 점검을 담당하는 파트너 회사와, 그들로부터 받은 문서를 관리합니다.',
        '파트너는 건물 × SS의 **기본 담당자**가 될 수 있습니다. 만기 구간이 열리면 통지 사이클이 해당 회사(및 IQP)에 방문 날짜를 요청하는 메일을 보냅니다.',
        '파트너 연락처는 SS 허브로 연결되어 Specified System 옆에 담당자 전화·주소가 표시됩니다.',
        '업로드한 파트너 서류는 다시 입력하지 말고 **Document extraction**으로 구조화된 데이터를 뽑아내세요.',
      ],
    },
  },

  'Document extraction': {
    en: {
      title: 'Document extraction',
      paragraphs: [
        'Upload a partner document (PDF or scan) and the extractor reads the fields it recognises — company, building, SS code, inspection date, result, signatory — so they do not have to be retyped.',
        'Extracted values are a **proposal**: review them before they are applied. The original file is always kept as the evidence copy.',
        'This is the same mechanism used for Compliance Schedule PDFs, where the extract also pulls SS section prose (performance standard, inspection procedures, reporting).',
        'If extraction fails, the error is surfaced as a toast — the upload itself still succeeds, so you can fill the fields manually and keep the document on file.',
      ],
    },
    ko: {
      title: '문서 추출',
      paragraphs: [
        '파트너 문서(PDF 또는 스캔)를 업로드하면 추출기가 인식 가능한 항목(회사, 건물, SS 코드, 점검일, 결과, 서명자)을 읽어 옵니다. 다시 입력할 필요가 없습니다.',
        '추출값은 **제안**입니다. 적용 전에 확인하세요. 원본 파일은 근거 사본으로 항상 보관됩니다.',
        'Compliance Schedule PDF에도 같은 방식이 쓰입니다. 그 경우 SS 섹션 본문(performance standard, 점검 절차, 보고 요건)까지 함께 추출합니다.',
        '추출이 실패하면 오류가 토스트로 표시됩니다. 업로드 자체는 성공하므로 항목을 수동으로 채우고 문서는 그대로 보관할 수 있습니다.',
      ],
    },
  },

  'IQP Register': {
    en: {
      title: 'IQP Register',
      paragraphs: [
        'Who is qualified for which Specified System. Each entry ties a person or company to an SS code with their IQP registration.',
        'The register is what makes the **default inspector** suggestion trustworthy: a building × SS can only be assigned to someone registered for that SS.',
        'Entries are listed in SS-number order (SS 1 → 2 → 3 → 3.1 → 4 → 10 → 15.2). Click an SS code for its handbook blurb — what the system is, what the inspection covers, and what evidence is required.',
        'Keep registrations current. An expired registration on a signed Form 12A is a compliance problem, not just a data problem.',
      ],
    },
    ko: {
      title: 'IQP 등록부',
      paragraphs: [
        '어떤 Specified System에 누가 자격이 있는지 관리합니다. 각 항목은 사람 또는 회사를 SS 코드와 IQP 등록번호로 연결합니다.',
        '이 등록부가 **기본 검사자** 추천을 신뢰할 수 있게 만듭니다. 건물 × SS는 그 SS에 등록된 사람에게만 배정할 수 있습니다.',
        '항목은 SS 번호순으로 정렬됩니다(SS 1 → 2 → 3 → 3.1 → 4 → 10 → 15.2). SS 코드를 클릭하면 핸드북 설명(시스템 정의, 점검 범위, 필요한 근거)을 볼 수 있습니다.',
        '등록 정보를 최신으로 유지하세요. 만료된 등록으로 서명된 Form 12A는 단순 데이터 문제가 아니라 컴플라이언스 문제입니다.',
      ],
    },
  },

  'Building Agent (Full BWOF)': {
    en: {
      title: 'Building Agent (Full BWOF)',
      paragraphs: [
        'Buildings where we act as the **full BWOF agent**: we own the whole annual cycle — every Specified System, the council pack and the certificate.',
        'For these buildings the inspection scope is FULL, so every SS on the Compliance Schedule appears on operational pages and must pass before the cycle can close.',
        'We coordinate everyone: in-house IQPs, partner companies for SS we do not cover, and experts for reports the IQP cannot sign.',
        'This is the mandate type to choose when the owner hands the whole BWOF obligation to us. Use **SS-Only Agent** when we cover only selected systems.',
      ],
    },
    ko: {
      title: '건물 대행 (BWOF 전체)',
      paragraphs: [
        '우리가 **BWOF 전체 대행사**로 활동하는 건물입니다. 연간 사이클 전체(모든 Specified System, 시청 제출 묶음, 증명서)를 책임집니다.',
        '이 건물의 inspection scope는 FULL입니다. Compliance Schedule의 모든 SS가 운영 화면에 나오고, 사이클을 닫으려면 전부 합격해야 합니다.',
        '자체 IQP, 우리가 다루지 않는 SS의 파트너 회사, IQP가 서명할 수 없는 리포트의 전문가까지 전체를 조율합니다.',
        '건물주가 BWOF 의무 전체를 우리에게 맡길 때 선택하는 위임 유형입니다. 일부 시스템만 담당한다면 **SS-Only Agent**를 사용하세요.',
      ],
    },
  },

  'SS-Only Agent': {
    en: {
      title: 'SS-Only Agent',
      paragraphs: [
        'Buildings where we service **selected Specified Systems only**. Another party (owner or a different agent) holds the overall BWOF.',
        'These buildings use inspection scope **PARTIAL**. Only the SS marked *include in inspection* appear on BWOF progress, Order, Units, Floor plan, Paperwork, Checklists, jobs and worker packs.',
        'Set the scope in **Edit Building**, which lists every SS on the Compliance Schedule so you can tick exactly what we cover.',
        'We issue Form 12A for our systems; we do not close the building’s BWOF cycle, because the SS outside our mandate are not ours to certify.',
      ],
    },
    ko: {
      title: 'SS 부분 대행',
      paragraphs: [
        '**지정한 Specified System만** 담당하는 건물입니다. 전체 BWOF는 건물주 또는 다른 대행사가 보유합니다.',
        'inspection scope는 **PARTIAL**입니다. *include in inspection*으로 체크한 SS만 BWOF progress, Order, Units, Floor plan, Paperwork, Checklists, jobs, 작업자 팩에 나타납니다.',
        '범위는 **Edit Building**에서 설정합니다. Compliance Schedule의 모든 SS가 나열되므로 담당 범위를 정확히 체크할 수 있습니다.',
        '우리가 담당하는 시스템에 대해 Form 12A를 발행합니다. 위임 범위 밖의 SS는 우리가 증명할 수 없으므로 건물의 BWOF 사이클을 닫지는 않습니다.',
      ],
    },
  },

  'How to add': {
    en: {
      title: 'How to add',
      paragraphs: [
        'Pick the building first, then choose the Specified Systems that belong to this mandate. The SS list comes from that building’s Compliance Schedule, so add the building and its CS before creating the mandate.',
        'For a **Full agent** mandate you do not tick individual systems — the mandate covers the whole Compliance Schedule.',
        'For an **SS-only** mandate, tick just the systems we service. That sets the building to PARTIAL scope and hides the rest from operational pages.',
        'Set the **default assignee** per SS (in-house IQP or partner company) and a contact email. The notify cycle uses it to request visit dates, so a mandate without an assignee will never auto-schedule.',
        'Save, then confirm on the building Overview that BWOF progress and SS Units show the expected systems.',
      ],
    },
    ko: {
      title: '추가 방법',
      paragraphs: [
        '먼저 건물을 고른 뒤 이 위임에 포함할 Specified System을 선택합니다. SS 목록은 그 건물의 Compliance Schedule에서 오므로, 위임을 만들기 전에 건물과 CS를 먼저 등록하세요.',
        '**Full agent** 위임은 개별 시스템을 체크하지 않습니다. Compliance Schedule 전체가 대상입니다.',
        '**SS-only** 위임은 담당하는 시스템만 체크합니다. 그러면 건물이 PARTIAL 범위가 되고 나머지는 운영 화면에서 숨겨집니다.',
        'SS별 **기본 담당자**(자체 IQP 또는 파트너 회사)와 연락 이메일을 설정하세요. 통지 사이클이 이 정보로 방문 날짜를 요청하므로, 담당자가 없는 위임은 자동 일정이 잡히지 않습니다.',
        '저장 후 건물 Overview에서 BWOF progress와 SS Units에 예상한 시스템이 나오는지 확인하세요.',
      ],
    },
  },

  /* --------------------------------------------------- Site inspection */

  'Site inspection feed': {
    en: {
      title: 'Site inspection feed',
      paragraphs: [
        'A live feed of site inspection points submitted by field workers, refreshed by polling roughly every 8 seconds while the API responds.',
        'Each entry shows the SS code, the situation (FIRST_VISIT / MONTHLY / SIX_MONTHLY / ANNUAL / CHANGE / GENERAL), the checkpoint, the worker’s note and any photos.',
        'These submissions are evidence, not just status. The photos and inspection text are later used as source material when producing **Form 11** and the **Site inspection report**; the originals stay in `site_inspection_point_results`.',
        'If the feed stays empty while workers are on site, check the API path first — see the 404 troubleshooting Info on this page.',
      ],
    },
    ko: {
      title: '현장 점검 피드',
      paragraphs: [
        '현장 작업자가 제출한 site inspection point를 실시간으로 보여 주는 피드입니다. API가 응답하는 동안 약 8초 주기로 폴링해 갱신합니다.',
        '각 항목에 SS 코드, 상황(FIRST_VISIT / MONTHLY / SIX_MONTHLY / ANNUAL / CHANGE / GENERAL), 체크포인트, 작업자 노트, 사진이 표시됩니다.',
        '이 제출물은 단순 상태가 아니라 근거 자료입니다. 사진과 점검 텍스트는 이후 **Form 11**과 **Site inspection report** 작성 시 원천 자료로 쓰이며, 원본은 `site_inspection_point_results`에 보존됩니다.',
        '작업자가 현장에 있는데 피드가 계속 비어 있으면 먼저 API 경로를 확인하세요. 이 화면의 404 문제 해결 Info를 참고하세요.',
      ],
    },
  },

  'Fixing 404 on site inspection API': {
    en: {
      title: 'Fixing 404 on site inspection API',
      paragraphs: [
        'A 404 here means the request reached a server that has no such route — almost always a **path or deployment** problem, not a data problem.',
        'Check the path prefix. The endpoints are `GET /api/site-inspection-points/definitions?ssCode=&situationKey=`, `POST /api/site-inspection-points/results` and `GET /api/site-inspection-points/results`. A missing `/api` prefix, or the app pointing at the web origin instead of the API base URL, produces exactly this 404.',
        'Confirm the API base URL. On the app that is `getApiBaseUrl` / `ApiClient.baseUrl`; in production the admin API runs under PM2 as `bwof-admin-api` on port 3010, while another service occupies port 3000.',
        'If the path is right, the build is probably stale: the route exists in source but was never deployed. Redeploy the backend and retry.',
        'Also verify migrations **033 / 034** ran, so `site_inspection_point_definitions` and `site_inspection_point_results` exist and the definitions seed is loaded. A missing table shows as a 500, not a 404 — if you see 500 instead, that is the thing to check.',
      ],
    },
    ko: {
      title: '현장 점검 API 404 해결',
      paragraphs: [
        '여기서 404는 요청이 서버에 도달했지만 그런 라우트가 없다는 뜻입니다. 거의 항상 데이터 문제가 아니라 **경로 또는 배포** 문제입니다.',
        '경로 접두사를 확인하세요. 엔드포인트는 `GET /api/site-inspection-points/definitions?ssCode=&situationKey=`, `POST /api/site-inspection-points/results`, `GET /api/site-inspection-points/results`입니다. `/api` 접두사가 빠졌거나 앱이 API 베이스가 아닌 웹 오리진을 가리키면 정확히 이 404가 납니다.',
        'API 베이스 URL을 확인하세요. 앱에서는 `getApiBaseUrl` / `ApiClient.baseUrl`입니다. 운영에서는 관리자 API가 PM2의 `bwof-admin-api`로 3010 포트에서 돌고, 3000 포트는 다른 서비스가 사용합니다.',
        '경로가 맞다면 빌드가 오래된 것일 가능성이 큽니다. 소스에는 라우트가 있지만 배포되지 않은 상태입니다. 백엔드를 재배포하고 다시 시도하세요.',
        '마이그레이션 **033 / 034** 적용 여부도 확인하세요. `site_inspection_point_definitions`, `site_inspection_point_results` 테이블과 정의 시드가 있어야 합니다. 테이블이 없으면 404가 아니라 500이 나므로, 500이 보이면 이쪽을 점검하세요.',
      ],
    },
  },

  'Site inspection': {
    en: {
      title: 'Site inspection',
      paragraphs: [
        'The worker checklist as the office sees it. Checkpoints are defined per **SS code × situation**, so a first visit asks different questions than a monthly or annual check.',
        'Workers open it from the job card in the app: pick SS and situation, then record a note and photos per checkpoint and submit. The admin view polls the same API so it stays close to what the worker just saved.',
        'Results are stored as evidence and reused later for Form 11 and the Site inspection report. Nothing is overwritten — amendments are added as new records.',
        'Pass / Incomplete / Fail colours (green / yellow / red) are the same here, on job rows, unit chips and app SS cards.',
      ],
    },
    ko: {
      title: '현장 점검',
      paragraphs: [
        '사무실에서 보는 작업자 체크리스트입니다. 체크포인트는 **SS 코드 × 상황**별로 정의되어 있어, 최초 방문과 월간·연간 점검의 질문이 서로 다릅니다.',
        '작업자는 앱의 Job 카드에서 열어 SS와 상황을 고르고, 체크포인트별로 노트와 사진을 기록해 제출합니다. 관리자 화면은 같은 API를 폴링하므로 작업자가 방금 저장한 내용과 거의 같게 유지됩니다.',
        '결과는 근거 자료로 저장되고 이후 Form 11, Site inspection report에 재사용됩니다. 덮어쓰지 않고 수정분은 새 레코드로 추가됩니다.',
        'Pass / Incomplete / Fail 색상(초록 / 노랑 / 빨강)은 여기와 작업 행, 유닛 칩, 앱 SS 카드에서 모두 동일합니다.',
      ],
    },
  },

  'Empty checklist — what to check': {
    en: {
      title: 'Empty checklist — what to check',
      paragraphs: [
        'An empty checklist means no questions resolved for this unit or SS. Work through the layers from the unit upward.',
        '**1. Unit snapshot.** Open the Unit hub → **Set Inspection List**. Inspection prefers the unit’s confirmed snapshot (`confirmed_checklist_json`); if nothing was ever confirmed, there is nothing to show.',
        '**2. Templates.** Check Settings → **Manage checklists** for this SS. If the set is empty, use *build default* to fill it from the Unit Standard pool, then Publish.',
        '**3. Subtype.** Questions are shared per equipment subtype. A unit placed under a subtype with no pool entries comes back empty — confirm the subtype is the right one.',
        '**4. Scope.** On a PARTIAL building, an SS not marked *include in inspection* is filtered out of operational data, so its checklist appears empty by design.',
        '**5. Situation.** Site inspection points are keyed by SS code **and** situation. Selecting a situation with no seeded definitions returns an empty list — try ANNUAL or GENERAL to confirm.',
      ],
    },
    ko: {
      title: '체크리스트가 비어 있을 때 확인할 것',
      paragraphs: [
        '체크리스트가 비었다는 것은 이 유닛 또는 SS에 대해 문항이 하나도 해석되지 않았다는 뜻입니다. 유닛에서 위로 단계별로 확인하세요.',
        '**1. 유닛 스냅샷.** Unit hub → **Set Inspection List**를 엽니다. 점검은 유닛의 확정 스냅샷(`confirmed_checklist_json`)을 우선 사용하므로, 확정한 적이 없으면 보여 줄 것이 없습니다.',
        '**2. 템플릿.** Settings → **Manage checklists**에서 해당 SS를 확인하세요. 세트가 비어 있으면 *build default*로 Unit Standard 풀에서 채운 뒤 Publish하세요.',
        '**3. 서브타입.** 문항은 장비 서브타입 단위로 공유됩니다. 풀 항목이 없는 서브타입에 배치된 유닛은 빈 결과가 나옵니다. 서브타입이 올바른지 확인하세요.',
        '**4. 검사 범위.** PARTIAL 건물에서 *include in inspection*이 아닌 SS는 운영 데이터에서 제외되므로, 설계상 체크리스트가 비어 보입니다.',
        '**5. 상황(situation).** Site inspection point는 SS 코드**와** 상황으로 조회합니다. 시드가 없는 상황을 고르면 빈 목록이 반환됩니다. ANNUAL 또는 GENERAL로 바꿔 확인해 보세요.',
      ],
    },
  },

  'Detection reports (FAIL)': {
    en: {
      title: 'Detection reports (FAIL)',
      paragraphs: [
        'When any checklist answer is **Fail**, the inspection cannot be certified. The worker is taken to the **Defect form** instead of Done.',
        'Flow in the app: Defect form → Preview (Save / Send / Print / Edit) → Confirm → the unit lands on the **Fail list** with a one-shot light-green acknowledgement. Units that are all Pass or N/A go to the Pass list.',
        'A Fail moves the inspection into **Repairing** — the same record continues as the repair track (quote → repair → re-inspect) until it passes.',
        'Form 12A requires IMR plus **all units passing**, so an outstanding Fail blocks the certificate for that SS and keeps the building’s BWOF cycle open.',
        'The defect record, photos and notes stay attached as the evidence trail for the repair and the eventual re-inspection.',
      ],
    },
    ko: {
      title: '불합격(FAIL) 검출 리포트',
      paragraphs: [
        '체크리스트 답변에 **Fail**이 하나라도 있으면 인스펙션을 증명할 수 없습니다. 작업자는 Done 대신 **Defect form**으로 이동합니다.',
        '앱 흐름: Defect form → 미리보기(Save / Send / Print / Edit) → Confirm → 해당 유닛이 **Fail list**로 이동하며 연한 초록으로 1회 확인 표시됩니다. 전부 Pass 또는 N/A인 유닛은 Pass list로 갑니다.',
        'Fail은 인스펙션을 **Repairing**으로 옮깁니다. 같은 레코드가 수리 트랙(견적 → 수리 → 재검사)으로 이어지며 합격할 때까지 유지됩니다.',
        'Form 12A는 IMR과 **전 유닛 합격**을 요구하므로, 미해결 Fail이 있으면 그 SS의 증명서가 막히고 건물의 BWOF 사이클이 열린 상태로 남습니다.',
        '하자 레코드, 사진, 노트는 수리와 재검사의 근거 이력으로 계속 첨부되어 있습니다.',
      ],
    },
  },

  'Site inspection API troubleshooting': {
    en: {
      title: 'Site inspection API troubleshooting',
      paragraphs: [
        'Read the status code first — it tells you which layer failed.',
        '**404** — route not found. Check the `/api/site-inspection-points/...` path and the API base URL, then redeploy if the route exists in source but not on the server.',
        '**401 / 403** — auth. The token expired, or the role is not allowed: submitting results is worker-only, while reading all results is admin (a worker only sees their own).',
        '**500** — usually the database. Confirm migrations **033 / 034** ran and the definitions seed is loaded (`npm run seed:site-inspection-sheets` in `web/backend`).',
        '**Empty but 200** — the query matched nothing. Definitions are keyed by SS code **and** situation key, so an unseeded combination legitimately returns an empty list.',
        '**Nothing at all / red link light** — the app cannot reach the API. Use the header link status **Check & fix**; the app pings the API via `Android.pingApi`.',
      ],
    },
    ko: {
      title: '현장 점검 API 문제 해결',
      paragraphs: [
        '먼저 상태 코드를 보세요. 어느 계층에서 실패했는지 알려 줍니다.',
        '**404** — 라우트 없음. `/api/site-inspection-points/...` 경로와 API 베이스 URL을 확인하고, 소스에는 있으나 서버에 없으면 재배포하세요.',
        '**401 / 403** — 인증·권한. 토큰이 만료되었거나 역할이 허용되지 않습니다. 결과 제출은 작업자 전용이고, 전체 결과 조회는 관리자 전용입니다(작업자는 본인 것만).',
        '**500** — 보통 데이터베이스입니다. 마이그레이션 **033 / 034** 적용과 정의 시드 로드를 확인하세요(`web/backend`에서 `npm run seed:site-inspection-sheets`).',
        '**200이지만 빈 응답** — 조건에 맞는 데이터가 없습니다. 정의는 SS 코드**와** 상황 키로 조회하므로, 시드가 없는 조합은 정상적으로 빈 목록을 반환합니다.',
        '**아무 응답도 없음 / 링크 표시등 빨강** — 앱이 API에 도달하지 못합니다. 헤더 링크 상태의 **Check & fix**를 사용하세요. 앱은 `Android.pingApi`로 API를 핑합니다.',
      ],
    },
  },

  /* ------------------------------------------------------- Application */

  Application: {
    en: {
      title: 'Application',
      paragraphs: [
        'The council application module: applying for a BWOF and amending a Compliance Schedule.',
        'Every application records a **flow path**: `AFTER_APPLY` (apply first, then do the work) or `BEFORE_APPLY` (work already done, legalise afterwards). The path changes the stage order, so set it correctly at the start.',
        'The Overview page explains the module’s role and the suggested order of its sub-menus; Apply BWOF and Amend CS are peer pages on the Line-2 sub-nav.',
        'Work is driven by **Process stages** — one current stage at a time, with completed stages available in a collapsible list.',
      ],
    },
    ko: {
      title: '신청',
      paragraphs: [
        '시청 신청 모듈입니다. BWOF 신청과 Compliance Schedule 변경을 다룹니다.',
        '모든 신청은 **flow path**를 기록합니다. `AFTER_APPLY`(먼저 신청하고 작업) 또는 `BEFORE_APPLY`(작업을 이미 완료하고 사후 합법화). 경로에 따라 단계 순서가 달라지므로 처음에 정확히 설정하세요.',
        'Overview 화면은 이 모듈의 역할과 하위 메뉴의 권장 순서를 설명합니다. Apply BWOF와 Amend CS는 두 번째 줄 서브 내비게이션의 동급 화면입니다.',
        '작업은 **Process stages**로 진행됩니다. 한 번에 현재 단계만 표시되고, 완료된 단계는 접이식 목록에서 확인합니다.',
      ],
    },
  },

  'Apply BWOF': {
    en: {
      title: 'Apply BWOF',
      paragraphs: [
        'Apply for the building’s annual Building Warrant of Fitness once the compliance work for the cycle is complete.',
        'Prerequisites: every in-scope Specified System inspected and **passing**, Form 12A issued per SS with IMR, and no SS left in Repairing.',
        'The application assembles the council pack from what is already in the system — building and CS details, per-SS certificates, and the inspection evidence behind them.',
        'The BWOF due date and the CS anniversary come from the building. If they are wrong, fix them on the building form (Auto-calculate or manual) before applying.',
        'After council approval the building cycle closes and dates roll forward for the next year.',
      ],
    },
    ko: {
      title: 'BWOF 신청',
      paragraphs: [
        '해당 사이클의 컴플라이언스 작업이 끝난 뒤 건물의 연간 Building Warrant of Fitness를 신청합니다.',
        '선행 조건: 검사 범위의 모든 Specified System 점검 완료 및 **합격**, SS별 Form 12A 발행(IMR 포함), Repairing 상태로 남은 SS 없음.',
        '신청서는 시스템에 이미 있는 자료로 시청 제출 묶음을 구성합니다. 건물·CS 정보, SS별 증명서, 그 근거가 되는 점검 기록.',
        'BWOF 만기일과 CS 기념일은 건물 정보에서 옵니다. 값이 잘못되어 있으면 신청 전에 건물 폼에서(자동 계산 또는 수동) 수정하세요.',
        '시청 승인 후 건물 사이클이 닫히고 날짜가 다음 해로 이월됩니다.',
      ],
    },
  },

  'Amend CS': {
    en: {
      title: 'Amend CS',
      paragraphs: [
        'Amend an existing Compliance Schedule — adding, removing or changing Specified Systems after a building change, alteration or correction.',
        'Start with **Step 0: choose the SS to work on** (search and multi-select from the full SS list), then pick **Path → Case type → Form / Process**. The chosen SS are written to `ac2109a_ss_codes` and drive the expert report guide and the expert pool filter.',
        'At the Path step, **← Change SS** returns to SS selection. **← Change scenario** keeps the saved application but saves current inputs, resets `workflow_step` to 0 and clears the process stages so Path and Case type can be chosen again. It does **not** create a new application.',
        'Process stages show one current stage at a time. Completing a stage advances automatically to the next and scrolls to the top; finished stages stay available under *view completed stages*.',
        'The **Expert Report** stage shows which expert type and which report each selected SS needs, and why.',
      ],
    },
    ko: {
      title: 'CS 변경',
      paragraphs: [
        '기존 Compliance Schedule을 변경합니다. 건물 변경·개조·정정에 따라 Specified System을 추가·삭제·수정합니다.',
        '**Step 0: 작업할 SS 선택**(전체 SS 목록에서 검색·다중 선택)으로 시작한 뒤 **Path → Case type → Form / Process** 순으로 진행합니다. 선택한 SS는 `ac2109a_ss_codes`에 반영되어 전문가 리포트 가이드와 전문가 풀 필터에 사용됩니다.',
        'Path 단계에서 **← Change SS**로 SS 선택으로 돌아갈 수 있습니다. **← Change scenario**는 저장된 신청서를 유지한 채 현재 입력을 저장하고 `workflow_step`을 0으로, process stage를 삭제해 Path와 Case type을 다시 고르게 합니다. 새 신청이 **아닙니다**.',
        'Process stage는 한 번에 현재 단계만 보여 줍니다. 단계를 완료하면 자동으로 다음 단계로 넘어가고 화면 상단으로 스크롤합니다. 완료한 단계는 *완료된 단계 보기*에서 확인합니다.',
        '**Expert Report** 단계에서는 선택한 SS별로 어떤 전문가와 어떤 리포트가 왜 필요한지 안내합니다.',
      ],
    },
  },

  'Building for this application': {
    en: {
      title: 'Building for this application',
      paragraphs: [
        'The building this application belongs to. Everything downstream — SS list, expert pool filter, certificates, council pack — is resolved from it.',
        'The Specified Systems offered here come from that building’s Compliance Schedule, so a missing or stale CS shows up as a missing SS.',
        'Changing the building mid-application is not a light edit: the SS selection, expert requests and stage evidence were all gathered against the previous building.',
        'If the building details are wrong, fix them on the building record rather than typing corrections into the application — the application reads the building, not the reverse.',
      ],
    },
    ko: {
      title: '이 신청의 대상 건물',
      paragraphs: [
        '이 신청서가 속한 건물입니다. 이후의 모든 것(SS 목록, 전문가 풀 필터, 증명서, 시청 제출 묶음)이 여기서 결정됩니다.',
        '여기 제시되는 Specified System은 그 건물의 Compliance Schedule에서 옵니다. CS가 없거나 오래되면 SS 누락으로 나타납니다.',
        '신청 도중 건물을 바꾸는 것은 가벼운 수정이 아닙니다. SS 선택, 전문가 요청, 단계별 근거가 모두 이전 건물 기준으로 수집되었기 때문입니다.',
        '건물 정보가 틀렸다면 신청서에 수정 내용을 적지 말고 건물 레코드를 고치세요. 신청서가 건물을 읽는 구조이며 그 반대가 아닙니다.',
      ],
    },
  },

  'Building & Compliance Schedule snapshot': {
    en: {
      title: 'Building & Compliance Schedule snapshot',
      paragraphs: [
        'A read-only summary of the building and its Compliance Schedule as they stand right now, shown so you can amend against facts rather than memory.',
        'It lists building basics, the CS anniversary and BWOF due date, and the Specified Systems with their CS prose — performance standard, inspection and maintenance procedures, and reporting requirements.',
        'Edits belong on the building record or the SS hub. This panel reflects the live data, so it updates once those are saved.',
        'If a Compliance Schedule PDF was imported, the SS section text here is what the extractor wrote into `building_ss_instances`. Compare it against the PDF before relying on it in an amendment.',
      ],
    },
    ko: {
      title: '건물 · Compliance Schedule 스냅샷',
      paragraphs: [
        '현재 시점의 건물과 Compliance Schedule을 읽기 전용으로 요약합니다. 기억이 아니라 실제 데이터를 기준으로 변경 작업을 하도록 제공합니다.',
        '건물 기본정보, CS 기념일과 BWOF 만기일, 그리고 Specified System별 CS 본문(performance standard, 점검·유지보수 절차, 보고 요건)을 보여 줍니다.',
        '수정은 건물 레코드나 SS 허브에서 합니다. 이 패널은 실시간 데이터를 반영하므로 저장 후 갱신됩니다.',
        'Compliance Schedule PDF를 가져왔다면, 여기의 SS 섹션 텍스트는 추출기가 `building_ss_instances`에 기록한 내용입니다. 변경 작업에 사용하기 전에 PDF와 대조해 확인하세요.',
      ],
    },
  },

  'Workflow & IQP field evidence': {
    en: {
      title: 'Workflow & IQP field evidence',
      paragraphs: [
        'This stage links the paper workflow to what the IQP actually saw on site. An amendment is only defensible if the field evidence supports it.',
        'Evidence comes from the worker app: site inspection points per SS and situation, with notes and photos, stored in `site_inspection_point_results` and never overwritten.',
        'That same material is the source for **Form 11** and the **Site inspection report**, so capture it properly during the visit rather than reconstructing it later.',
        'Complete this stage once the SS selection, the expert reports and the field evidence agree. Completing advances automatically to the next stage.',
      ],
    },
    ko: {
      title: '워크플로 · IQP 현장 근거',
      paragraphs: [
        '이 단계는 서류 워크플로와 IQP가 현장에서 실제로 확인한 내용을 연결합니다. 현장 근거가 뒷받침되지 않으면 변경 신청을 방어할 수 없습니다.',
        '근거는 작업자 앱에서 옵니다. SS·상황별 site inspection point에 노트와 사진이 함께 저장되며, `site_inspection_point_results`에 보존되고 덮어쓰지 않습니다.',
        '같은 자료가 **Form 11**과 **Site inspection report**의 원천이 됩니다. 나중에 재구성하지 말고 방문 중에 제대로 수집하세요.',
        'SS 선택, 전문가 리포트, 현장 근거가 일치하면 이 단계를 완료하세요. 완료하면 자동으로 다음 단계로 넘어갑니다.',
      ],
    },
  },

  'IQP site inspection — field evidence': {
    en: {
      title: 'IQP site inspection — field evidence',
      paragraphs: [
        'What the IQP recorded on site, kept as the evidence layer under this stage.',
        'Checkpoints are defined per **SS code × situation** (FIRST_VISIT / MONTHLY / SIX_MONTHLY / ANNUAL / CHANGE / GENERAL). The worker answers them in the app with a note and photos per checkpoint.',
        'Records are append-only. Later visits and amendments add rows instead of editing history, so the original observation stays auditable.',
        'This is the source material merged into **Form 11** and the **Site inspection report**. Missing photos or empty notes here become gaps in those documents.',
        'If nothing appears, the worker may not have submitted yet, or the situation you are viewing has no seeded checkpoints — check the API troubleshooting Info.',
      ],
    },
    ko: {
      title: 'IQP 현장 점검 — 현장 근거',
      paragraphs: [
        'IQP가 현장에서 기록한 내용입니다. 이 단계의 근거 자료 계층으로 보관됩니다.',
        '체크포인트는 **SS 코드 × 상황**(FIRST_VISIT / MONTHLY / SIX_MONTHLY / ANNUAL / CHANGE / GENERAL)별로 정의됩니다. 작업자가 앱에서 체크포인트마다 노트와 사진으로 답합니다.',
        '레코드는 추가 전용입니다. 이후 방문이나 수정은 이력을 고치지 않고 행을 추가하므로 최초 관찰 기록이 감사 가능한 상태로 남습니다.',
        '이 자료가 **Form 11**과 **Site inspection report**에 병합되는 원천입니다. 여기서 사진이 없거나 노트가 비면 그 문서에 그대로 공백이 됩니다.',
        '아무것도 안 보이면 작업자가 아직 제출하지 않았거나, 지금 보고 있는 상황에 시드된 체크포인트가 없는 경우입니다. API 문제 해결 Info를 확인하세요.',
      ],
    },
  },

  'Completing this stage': {
    en: {
      title: 'Completing this stage',
      paragraphs: [
        'Only one stage is shown at a time. When the work for it is done, complete it and the next stage opens automatically with the page scrolled to the top.',
        'Completed stages are not hidden — open *view completed stages* to review or re-check what was recorded.',
        'Completion is a record, not just navigation: the stage is stored against this application (`compliance_amendment_stages`), so the sequence of decisions stays auditable.',
        'Do not complete a stage to “park” it. Use **← Change scenario** if the path or case type was wrong; that resets the stages while keeping the saved application.',
      ],
    },
    ko: {
      title: '이 단계 완료하기',
      paragraphs: [
        '한 번에 한 단계만 표시됩니다. 해당 작업이 끝나면 완료 처리하세요. 다음 단계가 자동으로 열리고 화면이 상단으로 스크롤됩니다.',
        '완료된 단계는 숨겨지지 않습니다. *완료된 단계 보기*에서 기록을 다시 확인할 수 있습니다.',
        '완료는 단순 화면 이동이 아니라 기록입니다. 단계가 이 신청서에 저장되므로(`compliance_amendment_stages`) 의사결정 순서가 감사 가능한 상태로 남습니다.',
        '단계를 “보류”하려고 완료 처리하지 마세요. Path나 Case type이 잘못됐다면 **← Change scenario**를 사용하세요. 저장된 신청서는 유지하면서 단계만 초기화합니다.',
      ],
    },
  },

  'Process workflow': {
    en: {
      title: 'Process workflow',
      paragraphs: [
        'The staged path an application follows. Stages depend on the **flow path** (`AFTER_APPLY` or `BEFORE_APPLY`) and on the case type, so the list is not the same for every job.',
        'The screen deliberately shows only the **current stage** as a large card. That keeps attention on the one action that is actually due.',
        'Completing a stage records it and advances automatically. Completed stages fold into a collapsible list you can reopen at any time.',
        'Typical stages include SS selection, expert reports, IQP site inspection and field evidence, document preparation and council submission — up to 16 stage slots are supported.',
      ],
    },
    ko: {
      title: '진행 워크플로',
      paragraphs: [
        '신청서가 따라가는 단계 경로입니다. 단계 구성은 **flow path**(`AFTER_APPLY` 또는 `BEFORE_APPLY`)와 case type에 따라 달라지므로 모든 건이 같은 목록을 쓰지 않습니다.',
        '이 화면은 의도적으로 **현재 단계만** 큰 카드로 보여 줍니다. 실제로 해야 할 하나의 작업에 집중하도록 하기 위함입니다.',
        '단계를 완료하면 기록되고 자동으로 다음 단계로 넘어갑니다. 완료된 단계는 접이식 목록으로 들어가며 언제든 다시 열 수 있습니다.',
        '대표적인 단계는 SS 선택, 전문가 리포트, IQP 현장 점검·근거, 서류 준비, 시청 제출입니다. 최대 16개 단계 슬롯을 지원합니다.',
      ],
    },
  },

  'SS workflow': {
    en: {
      title: 'SS workflow',
      paragraphs: [
        'The per-SS route from visit to certificate: inspect the units → judge each checklist item Pass / Fail / N/A → all pass means the SS is done → preview → IQP signature → issue.',
        'Issuing produces **Form 12A** (the short legal certificate, requiring IMR and all units passing) and the **Inspection test report** (the detailed per-unit checklist), filed together under `Form-12A/{date}/`.',
        'Any Fail diverts to the defect form and the SS enters Repairing until re-inspection passes. The SS cannot be certified while a Fail is open.',
        'After issue the SS is **locked** in the app. Changes go through Building → Paperwork → **Amend**, which supersedes the old documents and re-issues.',
      ],
    },
    ko: {
      title: 'SS 워크플로',
      paragraphs: [
        'SS 하나의 방문에서 증명서까지의 경로입니다. 유닛 점검 → 항목별 Pass / Fail / N/A 판정 → 전부 합격이면 SS 완료 → 미리보기 → IQP 서명 → 발행.',
        '발행 결과물은 **Form 12A**(IMR과 전 유닛 합격이 필요한 간단 법적 증명서)와 **Inspection test report**(유닛별 상세 체크리스트)이며 `Form-12A/{날짜}/` 폴더에 함께 저장됩니다.',
        'Fail이 하나라도 있으면 하자 양식으로 분기되고 그 SS는 재검사 합격까지 Repairing에 머무릅니다. Fail이 열려 있는 동안에는 증명할 수 없습니다.',
        '발행 후 앱에서 해당 SS가 **잠깁니다**. 변경은 Building → Paperwork → **Amend**로 진행하며 기존 문서를 대체하고 재발행합니다.',
      ],
    },
  },

  'SS workflow (amendment)': {
    en: {
      title: 'SS workflow (amendment)',
      paragraphs: [
        'The amendment variant: the SS was already certified, and the site visit exists to support a Compliance Schedule change rather than a routine renewal.',
        'The visit is recorded with situation **CHANGE**, so the checkpoints asked are the change-specific ones, not the monthly or annual set.',
        'Evidence requirements are higher. Notes and photos from this visit are the justification the council sees through Form 11 and the Site inspection report, so record what changed and why the amended arrangement complies.',
        'Certificates already issued are not edited in place. Re-issue through Paperwork → **Amend**, which supersedes the superseded documents and keeps both versions traceable.',
      ],
    },
    ko: {
      title: 'SS 워크플로 (변경)',
      paragraphs: [
        '변경용 흐름입니다. 해당 SS는 이미 증명이 발행된 상태이며, 이번 현장 방문은 정기 갱신이 아니라 Compliance Schedule 변경을 뒷받침하기 위한 것입니다.',
        '방문은 상황 **CHANGE**로 기록됩니다. 따라서 월간·연간 세트가 아니라 변경 전용 체크포인트가 제시됩니다.',
        '근거 요건이 더 높습니다. 이번 방문의 노트와 사진이 Form 11과 Site inspection report를 통해 시청에 제시되는 근거이므로, 무엇이 바뀌었고 변경된 구성이 왜 적합한지 기록하세요.',
        '이미 발행된 증명서는 그 자리에서 수정하지 않습니다. Paperwork → **Amend**로 재발행하면 기존 문서를 대체하면서 두 버전을 모두 추적할 수 있습니다.',
      ],
    },
  },

  /* ---------------------------------------------------------- Settings */

  Settings: {
    en: {
      title: 'Settings',
      paragraphs: [
        'Company-level configuration. The sub-nav starts with **Overview**, then the peer pages including **Checklist templates** and **DB Setting**.',
        'Here you set the company profile and logo (PNG / JPEG / WebP / GIF, max 2 MB), enable optional modules such as **Chiller**, seed document form templates, and run the auto due cycle on demand.',
        'BWOF dates are **not** company settings — each building sets its own due date and CS anniversary on the building form.',
        'Company documents live in a cabinet on disk (`{CompanyName}/document/` plus category folders), configured by `COMPANY_CABINET_PATH` / `DOCUMENTS_PATH`.',
        'Office ADMIN / MANAGER / MASTER (and worker accounts with access group Admin) keep edit authority on these fields.',
      ],
    },
    ko: {
      title: '설정',
      paragraphs: [
        '회사 단위 설정입니다. 서브 내비게이션은 **Overview**로 시작하고, **Checklist templates**와 **DB Setting** 등이 동급 화면으로 이어집니다.',
        '여기서 회사 프로필과 로고(PNG / JPEG / WebP / GIF, 최대 2 MB)를 설정하고, **Chiller** 같은 선택 모듈을 켜고, 문서 양식 템플릿을 시드하고, 자동 만기 사이클을 즉시 실행할 수 있습니다.',
        'BWOF 날짜는 회사 설정이 **아닙니다**. 건물마다 건물 폼에서 만기일과 CS 기념일을 직접 설정합니다.',
        '회사 문서는 디스크의 캐비닛(`{회사명}/document/` + 카테고리 폴더)에 보관되며 `COMPANY_CABINET_PATH` / `DOCUMENTS_PATH`로 설정합니다.',
        '사무실 ADMIN / MANAGER / MASTER(및 접근 그룹이 Admin인 작업자 계정)는 이 항목의 편집 권한을 유지합니다.',
      ],
    },
  },

  'Checklist templates': {
    en: {
      title: 'Checklist templates',
      paragraphs: [
        'Master checklist sets per Specified System. Everything a worker answers in the field starts here.',
        'Content comes from the **Unit Standard pool**: *build default* fills the mandatory items, then add from the pool, remove what does not apply, reorder, or write custom items.',
        'Header tabs: **Overview** (read-only list) · **Edit** (questions with Save / Revert / Publish and a Cancel·OK confirm) · **Add question** (manual or file upload) · **Preview on app** (exactly as the worker’s phone shows it) · **Info** (English field help).',
        'Each question can carry long how-to text (**Info**) and guidance photos. Those follow the question through pool → unit snapshot → worker app.',
        'Templates are not retroactive. Units keep the snapshot confirmed on the Unit hub, so publishing a change does not rewrite work already done.',
      ],
    },
    ko: {
      title: '체크리스트 템플릿',
      paragraphs: [
        'Specified System별 마스터 체크리스트 세트입니다. 작업자가 현장에서 답하는 모든 항목이 여기서 시작합니다.',
        '내용은 **Unit Standard 풀**에서 옵니다. *build default*로 필수 항목을 채운 뒤 풀에서 추가, 불필요 항목 제거, 순서 변경, 커스텀 항목 작성이 가능합니다.',
        '헤더 탭: **Overview**(읽기 전용 목록) · **Edit**(문항 편집, Save / Revert / Publish + Cancel·OK 확인) · **Add question**(직접 입력 또는 파일 업로드) · **Preview on app**(작업자 휴대폰과 동일한 화면) · **Info**(영문 항목 설명).',
        '문항마다 상세 설명(**Info**)과 안내 사진을 붙일 수 있습니다. 이 내용은 풀 → 유닛 스냅샷 → 작업자 앱으로 함께 전달됩니다.',
        '템플릿은 소급 적용되지 않습니다. 유닛은 Unit hub에서 확정한 스냅샷을 유지하므로, 변경을 Publish해도 이미 수행된 작업이 바뀌지 않습니다.',
      ],
    },
  },

  'DB Setting': {
    en: {
      title: 'DB Setting',
      paragraphs: [
        'A catalogue of the live database tables with a plain-language purpose for each one. Filter by table name or purpose to find where something is stored.',
        'The catalogue is **visible without unlocking**. The password unlock is only required to edit data or structure.',
        'This is a maintenance tool, not a normal workflow. Direct edits bypass application validation — prefer the proper screen whenever one exists.',
        'Useful when checking whether a screen is reading the right source: SS catalogue = `building_ss_instances`, devices = `ss_units`, subtypes = `ss_instance_equipment_types`, checklist templates = `checklist_sets`.',
      ],
    },
    ko: {
      title: 'DB 설정',
      paragraphs: [
        '실제 데이터베이스 테이블 목록과 각 테이블의 용도를 쉬운 설명으로 제공합니다. 테이블명 또는 용도로 검색해 어디에 저장되는지 찾을 수 있습니다.',
        '카탈로그는 **잠금 해제 없이 조회**할 수 있습니다. 비밀번호 해제는 데이터나 구조를 편집할 때만 필요합니다.',
        '일반 업무용이 아닌 유지보수 도구입니다. 직접 편집은 애플리케이션 검증을 우회하므로, 전용 화면이 있으면 그쪽을 사용하세요.',
        '화면이 올바른 출처를 읽는지 확인할 때 유용합니다. SS 카탈로그 = `building_ss_instances`, 장치 = `ss_units`, 서브타입 = `ss_instance_equipment_types`, 체크리스트 템플릿 = `checklist_sets`.',
      ],
    },
  },

  /* -------------------------------------------------- Service network */

  'Registered Company': {
    en: {
      title: 'Registered Company',
      paragraphs: [
        'The service network: every organisation we work with — partner inspection companies, IQP partners, engineers and agents.',
        'Overview is a section hub; **Our Partner**, **Partly agent**, **Agent**, **IQP** and **Engineers** are peer pages on the sub-nav, and Overview explains the recommended order to work through them.',
        'From a company you can drill down: company basics → the buildings and Specified Systems it is mandated for → that building’s all-SS history → the SS date list.',
        'Registering a company also creates its document cabinet folders, so paperwork received from that company has a home from day one.',
      ],
    },
    ko: {
      title: '등록 업체',
      paragraphs: [
        '서비스 네트워크입니다. 함께 일하는 모든 조직(파트너 점검 회사, IQP 파트너, 엔지니어, 대행사)을 관리합니다.',
        'Overview는 섹션 허브이고, **Our Partner**, **Partly agent**, **Agent**, **IQP**, **Engineers**가 서브 내비게이션의 동급 화면입니다. Overview에서 권장 진행 순서를 설명합니다.',
        '업체에서 단계적으로 내려갈 수 있습니다. 업체 기본정보 → 위임받은 건물과 Specified System → 그 건물의 전체 SS 이력 → SS 날짜 목록.',
        '업체를 등록하면 문서 캐비닛 폴더도 함께 생성되어, 그 업체에서 받은 서류를 처음부터 정해진 위치에 보관할 수 있습니다.',
      ],
    },
  },

  'Building SS history': {
    en: {
      title: 'Building SS history',
      paragraphs: [
        'Every Specified System on this building with its inspection history — who inspected, when, the result, and the certificate issued.',
        'Reached by drilling down from a partner company: company → mandated buildings → this all-SS history → the SS date list.',
        'Use it to answer “when was this system last done and by whom”, and to spot systems drifting out of their frequency.',
        'Rows are ordered by SS number (SS 1 → 2 → 3 → 3.1 → 4 → 10 → 15.2), the same ordering used across web, API and app.',
      ],
    },
    ko: {
      title: '건물 SS 이력',
      paragraphs: [
        '이 건물의 모든 Specified System과 점검 이력입니다. 누가·언제 점검했고 결과가 무엇이며 어떤 증명서가 발행되었는지 보여 줍니다.',
        '파트너 업체에서 단계적으로 들어옵니다. 업체 → 위임 건물 → 이 전체 SS 이력 → SS 날짜 목록.',
        '“이 시스템을 마지막으로 언제 누가 했는가”를 확인하고, 주기를 벗어나기 시작한 시스템을 찾아내는 데 사용하세요.',
        '행은 SS 번호순(SS 1 → 2 → 3 → 3.1 → 4 → 10 → 15.2)으로 정렬됩니다. 웹·API·앱에서 동일한 정렬 규칙을 씁니다.',
      ],
    },
  },

  'SS date history': {
    en: {
      title: 'SS date history',
      paragraphs: [
        'The dated timeline for one Specified System on one building: each visit, its result, and the paperwork it produced.',
        'This is the deepest level of the partner drill-down and the fastest way to verify frequency compliance — monthly, quarterly, semi-annual or annual, as set by the Compliance Schedule.',
        'Where the Compliance Schedule is silent on frequency, the Unit Standard requirement applies. Check the SS hub if a date pattern looks wrong.',
        'Failures appear in place, with the repair track that followed, so a gap in the timeline is explainable rather than mysterious.',
      ],
    },
    ko: {
      title: 'SS 날짜 이력',
      paragraphs: [
        '한 건물의 특정 Specified System에 대한 날짜별 타임라인입니다. 각 방문과 결과, 생성된 서류를 보여 줍니다.',
        '파트너 드릴다운의 가장 깊은 단계이며, 주기 준수를 확인하는 가장 빠른 방법입니다. 주기는 Compliance Schedule에 따라 월간·분기·반기·연간입니다.',
        'Compliance Schedule에 주기가 명시되지 않은 경우 Unit Standard 요건을 따릅니다. 날짜 패턴이 이상하면 SS 허브를 확인하세요.',
        '불합격은 이어진 수리 트랙과 함께 그 자리에 표시되므로, 타임라인의 공백이 설명 가능한 형태로 남습니다.',
      ],
    },
  },

  'IQP partners': {
    en: {
      title: 'IQP partners',
      paragraphs: [
        'External IQPs and IQP companies we assign work to, with the Specified Systems each is registered for.',
        'An IQP partner can be the **default assignee** on a building × SS. When the due window opens, the notify cycle emails the IQP and the company together and waits for a visit date.',
        'Registration per SS is what makes the assignment valid — only someone registered for that SS may sign its Form 12A.',
        'Keep contact details current: they appear on the SS hub next to the system, and they are the address the notify emails go to.',
      ],
    },
    ko: {
      title: 'IQP 파트너',
      paragraphs: [
        '작업을 배정하는 외부 IQP 및 IQP 업체입니다. 각자 등록된 Specified System과 함께 관리합니다.',
        'IQP 파트너는 건물 × SS의 **기본 담당자**가 될 수 있습니다. 만기 구간이 열리면 통지 사이클이 IQP와 업체에 함께 메일을 보내고 방문 날짜를 기다립니다.',
        'SS별 등록이 배정의 유효성을 결정합니다. 해당 SS에 등록된 사람만 그 SS의 Form 12A에 서명할 수 있습니다.',
        '연락처를 최신으로 유지하세요. SS 허브에서 시스템 옆에 표시되고, 통지 메일이 발송되는 주소이기도 합니다.',
      ],
    },
  },

  'Partner company': {
    en: {
      title: 'Partner company',
      paragraphs: [
        'One partner organisation. Basics (name, address, contacts) plus the buildings and Specified Systems it is mandated for.',
        'From the mandate list you can go to a building’s all-SS history and then to the SS date list, which is the quickest way to review a partner’s actual performance.',
        'Contact details flow into the SS hub, so the assignee phone and address show next to the system the partner services.',
        'Documents received from this company are filed in its cabinet folders; use Document extraction rather than retyping their reports.',
      ],
    },
    ko: {
      title: '파트너 업체',
      paragraphs: [
        '파트너 조직 1곳입니다. 기본정보(이름, 주소, 담당자)와 위임받은 건물·Specified System을 보여 줍니다.',
        '위임 목록에서 건물의 전체 SS 이력, 이어서 SS 날짜 목록으로 이동할 수 있습니다. 파트너의 실제 수행 상태를 확인하는 가장 빠른 경로입니다.',
        '연락처는 SS 허브로 연결되어, 그 파트너가 담당하는 시스템 옆에 담당자 전화와 주소가 표시됩니다.',
        '이 업체에서 받은 문서는 해당 캐비닛 폴더에 보관됩니다. 리포트를 다시 입력하지 말고 Document extraction을 사용하세요.',
      ],
    },
  },

  'Our Partner': {
    en: {
      title: 'Our Partner',
      paragraphs: [
        'Companies we partner with to deliver inspections we do not perform in-house — the supply side of the service network.',
        'Register the company, its contacts, and the Specified Systems it covers. Those SS decide where it can be set as default assignee.',
        'A partner is an external party: no app login. Coordination is by notify email and by the paperwork they return, which is filed against the building.',
        'Drill into a company to see its mandated buildings and SS, then the per-building SS history and date list.',
      ],
    },
    ko: {
      title: '우리 파트너',
      paragraphs: [
        '자체 수행하지 않는 점검을 함께 수행하는 파트너 업체입니다. 서비스 네트워크의 공급 측입니다.',
        '업체와 담당자, 담당하는 Specified System을 등록하세요. 그 SS가 기본 담당자로 설정 가능한 범위를 결정합니다.',
        '파트너는 외부 당사자이므로 앱 로그인이 없습니다. 조율은 통지 메일과, 그들이 회신하는 서류(건물에 귀속되어 보관)로 이루어집니다.',
        '업체로 들어가면 위임 건물과 SS를 볼 수 있고, 이어서 건물별 SS 이력과 날짜 목록으로 이동합니다.',
      ],
    },
  },

  'Registered IQP list': {
    en: {
      title: 'Registered IQP list',
      paragraphs: [
        'All registered IQPs visible to this company — internal staff and external partners together — with the SS codes each holds.',
        'This list is what the assignment and notify logic trusts. If an IQP is missing here, they cannot be set as the default inspector for a Specified System.',
        'Entries appear in SS-number order. Click an SS code to read its handbook blurb: what the system is, what the inspection covers, and what evidence is required.',
        'Internal IQPs also have a worker account (app login, SS grades, building assignments); external ones are contacted by email only.',
      ],
    },
    ko: {
      title: '등록 IQP 목록',
      paragraphs: [
        '이 회사에서 조회 가능한 등록 IQP 전체입니다. 내부 직원과 외부 파트너를 함께 보여 주고, 각자 보유한 SS 코드를 표시합니다.',
        '배정과 통지 로직이 신뢰하는 목록입니다. 여기에 없는 IQP는 Specified System의 기본 검사자로 설정할 수 없습니다.',
        '항목은 SS 번호순으로 표시됩니다. SS 코드를 클릭하면 핸드북 설명(시스템 정의, 점검 범위, 필요한 근거)을 볼 수 있습니다.',
        '내부 IQP는 작업자 계정(앱 로그인, SS 등급, 건물 배정)도 함께 가집니다. 외부 IQP는 이메일로만 연락합니다.',
      ],
    },
  },

  'Engineer vs IQP': {
    en: {
      title: 'Engineer vs IQP',
      paragraphs: [
        'They answer different questions, and the difference decides who you contact.',
        'An **IQP** is Independently Qualified — registered with the council for specific Specified Systems, and the person who inspects those systems and signs **Form 12A** for them.',
        'An **engineer / expert** produces a specialist report (fire, mechanical, electrical, acoustic) that the IQP cannot produce alone. It is evidence, not a certificate.',
        'Typical case: a Compliance Schedule amendment needs a fire engineering brief before the IQP can certify the amended arrangement. Engage the engineer for the report, the IQP for the inspection and certificate.',
        'The SS expert report guide lists, per SS, which expert type and which report is needed and why. Registration for signing lives in the IQP Register.',
      ],
    },
    ko: {
      title: '엔지니어와 IQP의 차이',
      paragraphs: [
        '두 역할은 다른 질문에 답하며, 그 차이가 누구에게 연락할지를 결정합니다.',
        '**IQP**는 Independently Qualified Person으로, 특정 Specified System에 대해 시청에 등록되어 있습니다. 해당 시스템을 점검하고 **Form 12A**에 서명하는 주체입니다.',
        '**엔지니어 / 전문가**는 IQP 단독으로 작성할 수 없는 전문 리포트(소방, 기계, 전기, 음향)를 작성합니다. 이는 증명서가 아니라 근거 자료입니다.',
        '전형적인 경우: Compliance Schedule 변경 시 IQP가 변경된 구성을 증명하기 전에 소방 엔지니어링 브리프가 필요합니다. 리포트는 엔지니어에게, 점검과 증명은 IQP에게 맡깁니다.',
        'SS별로 어떤 전문가와 어떤 리포트가 왜 필요한지는 SS 전문가 리포트 가이드에 있습니다. 서명 자격 등록은 IQP 등록부에서 관리합니다.',
      ],
    },
  },

  'Partner companies': {
    en: {
      title: 'Partner companies',
      paragraphs: [
        'Agent-side view of partner organisations: who is mandated for which buildings and Specified Systems.',
        'Click a company to open its basics and mandate list, then drill into a building’s all-SS history and the SS date list.',
        'A company set as default assignee receives the notify email when the due window opens, together with the IQP. Without an assignee, nothing auto-schedules.',
        'Coverage gaps are worth checking here: a Specified System with no partner and no internal IQP will sit in Due queue with nobody to ask for a date.',
      ],
    },
    ko: {
      title: '파트너 업체 목록',
      paragraphs: [
        '대행 관점에서 본 파트너 조직 목록입니다. 어떤 건물과 Specified System에 누가 위임되어 있는지 보여 줍니다.',
        '업체를 클릭하면 기본정보와 위임 목록이 열리고, 이어서 건물의 전체 SS 이력과 SS 날짜 목록으로 들어갈 수 있습니다.',
        '기본 담당자로 설정된 업체는 만기 구간이 열릴 때 IQP와 함께 통지 메일을 받습니다. 담당자가 없으면 자동 일정이 잡히지 않습니다.',
        '여기서 담당 공백을 점검하면 좋습니다. 파트너도 내부 IQP도 없는 Specified System은 날짜를 요청할 대상이 없어 Due queue에 그대로 남습니다.',
      ],
    },
  },

  /* ------------------------------------------------------ Building form */

  'Owner / Customer (from CS)': {
    en: {
      title: 'Owner / Customer (from CS)',
      paragraphs: [
        'Choose whether this building’s inspection covers the whole Compliance Schedule (**Full**) or only some Specified Systems (**Partial**, for example SS 9).',
        'Owner name, phone, email and mailing address sit under that choice. They are usually filled from the Compliance Schedule PDF and can be edited here.',
        '**Partial** can record an External Agent: the company that holds the rest of the BWOF. Pick one from the list, or enter company name, phone, email and address. Those fields are optional.',
        '**Full** hides the External Agent. The Specified Systems Full / Partial radios follow this choice and cannot be changed on that list.',
      ],
    },
    ko: {
      title: '소유자 / 고객 (CS)',
      paragraphs: [
        '이 건물 점검을 Compliance Schedule **전체(Full)**로 할지, 일부 Specified System만(**Partial**, 예: SS 9) 할지 먼저 고릅니다.',
        '그 아래에 소유자 이름, 전화, 이메일, 우편 주소가 있습니다. 보통 Compliance Schedule PDF에서 채워지며 여기서 고칠 수 있습니다.',
        '**Partial**이면 External Agent를 적을 수 있습니다. 나머지 BWOF를 맡은 회사입니다. 목록에서 고르거나, 회사명·전화·이메일·주소를 직접 넣습니다. 이 칸은 선택입니다.',
        '**Full**이면 External Agent는 보이지 않습니다. Specified Systems의 Full / Partial 라디오는 이 선택을 따라가며 그 목록에서는 바꿀 수 없습니다.',
      ],
    },
  },

  'External Agent': {
    en: {
      title: 'External Agent',
      paragraphs: [
        'Optional when inspection coverage is **Partial**. This company holds the rest of the BWOF for the systems we do not inspect.',
        'Pick a company already on the External Agent list, or enter the company name, phone, email and address. A new company is saved under Registered Company → Partly agent.',
        'Paperwork uses this contact when the pack is sent on instead of lodged by us.',
      ],
    },
    ko: {
      title: 'External Agent',
      paragraphs: [
        '점검 범위가 **Partial**일 때 선택입니다. 우리가 점검하지 않는 시스템의 BWOF를 맡은 회사입니다.',
        'External Agent 목록에 있는 회사를 고르거나, 회사명·전화·이메일·주소를 직접 넣습니다. 새 회사는 Registered Company → Partly agent에 저장됩니다.',
        '우리가 시청에 제출하지 않고 서류를 넘길 때 Paperwork가 이 연락처를 사용합니다.',
      ],
    },
  },

  'Specified Systems (SS)': {
    en: {
      title: 'Specified Systems (SS)',
      paragraphs: [
        'Full or Partial is chosen under Owner / Customer. The Inspection scope radios follow that choice and stay disabled.',
        'Uncheck any SS while Full and the building switches to Partial. Tick every SS and it switches back to Full. On Partial, tick the SS in scope and Save.',
        'Click an SS row for CS settings and Default IQP.',
      ],
    },
    ko: {
      title: 'Specified Systems (SS)',
      paragraphs: [
        'Full 또는 Partial은 Owner / Customer에서 고릅니다. Inspection scope 라디오는 그 선택을 따라가며 여기서는 바꿀 수 없습니다.',
        'Full인 상태에서 SS 체크를 하나라도 끄면 Partial로 바뀝니다. SS를 모두 켜면 다시 Full이 됩니다. Partial이면 범위에 넣을 SS를 고른 뒤 Save합니다.',
        'SS 행을 누르면 CS 설정과 Default IQP로 이동합니다.',
      ],
    },
  },

  'Building Information': {
    en: {
      title: 'Building Information',
      paragraphs: [
        'Edit Building captures **name, address, BWOF dates, Compliance Schedule number**, and initial inspection status.',
        '**CS Issue Date** is the BWOF due anchor. The next due date uses that month and day: this year if it has not passed, otherwise next year.',
        '**Contract / start date** is used only for paperwork years on SS forms. Inspection due dates stay on the CS / SS anniversary. It does not defer the building or remove it from work lists.',
        '**Full / Partial** is chosen under **Owner / Customer**. Full covers the whole building. Partial covers selected SS only. An External Agent can be recorded and is optional. The Specified Systems radios follow that choice and stay disabled.',
        'Parts, levels and drawings are set in **Building structure & drawings**. SS unit placement is on **Floor plan**.',
      ],
    },
    ko: {
      title: '건물 정보',
      paragraphs: [
        'Edit Building에서는 **이름, 주소, BWOF 날짜, Compliance Schedule 번호**, 초기 점검 상태를 입력합니다.',
        '**CS Issue Date**가 BWOF 만기 기준입니다. 그 월·일로 다음 만기일을 넣습니다. 아직 안 지났으면 올해, 지났으면 내년입니다.',
        '**Contract / start date**는 서류(SS forms) 연도 계산에만 씁니다. 점검 Due date는 CS / SS 기념일을 그대로 씁니다. 건물을 미루거나 작업 목록에서 빼지 않습니다.',
        '**Full / Partial**은 **Owner / Customer**에서 고릅니다. Full은 건물 전체, Partial은 고른 SS만 담당합니다. External Agent는 선택입니다. Specified Systems의 라디오는 그 선택을 따라가며 여기서는 바꿀 수 없습니다.',
        '동·층·도면은 **Building structure & drawings**에서 설정합니다. SS 유닛 배치는 **Floor plan**에서 합니다.',
      ],
    },
  },

  'Building basics': {
    en: {
      title: 'Building Information',
      paragraphs: [
        'Edit Building captures **name, address, BWOF dates, Compliance Schedule number**, and initial inspection status.',
        '**CS Issue Date** is the BWOF due anchor. The next due date uses that month and day: this year if it has not passed, otherwise next year.',
        '**Contract / start date** is used only for paperwork years on SS forms. Inspection due dates stay on the CS / SS anniversary. It does not defer the building or remove it from work lists.',
        '**Full / Partial** is chosen under **Owner / Customer**. Full covers the whole building. Partial covers selected SS only. An External Agent can be recorded and is optional. The Specified Systems radios follow that choice and stay disabled.',
        'Parts, levels and drawings are set in **Building structure & drawings**. SS unit placement is on **Floor plan**.',
      ],
    },
    ko: {
      title: '건물 정보',
      paragraphs: [
        'Edit Building에서는 **이름, 주소, BWOF 날짜, Compliance Schedule 번호**, 초기 점검 상태를 입력합니다.',
        '**CS Issue Date**가 BWOF 만기 기준입니다. 그 월·일로 다음 만기일을 넣습니다. 아직 안 지났으면 올해, 지났으면 내년입니다.',
        '**Contract / start date**는 서류(SS forms) 연도 계산에만 씁니다. 점검 Due date는 CS / SS 기념일을 그대로 씁니다. 건물을 미루거나 작업 목록에서 빼지 않습니다.',
        '**Full / Partial**은 **Owner / Customer**에서 고릅니다. Full은 건물 전체, Partial은 고른 SS만 담당합니다. External Agent는 선택입니다. Specified Systems의 라디오는 그 선택을 따라가며 여기서는 바꿀 수 없습니다.',
        '동·층·도면은 **Building structure & drawings**에서 설정합니다. SS 유닛 배치는 **Floor plan**에서 합니다.',
      ],
    },
  },

  'Compliance Schedule PDF': {
    en: {
      title: 'Compliance Schedule PDF',
      paragraphs: [
        'Upload the building’s Compliance Schedule and the extractor reads it instead of you typing it: building fields, SS instances, units, and optionally a checklist seed.',
        'On an **existing** building the extract is **applied automatically** so Detail, SS Units and the SS hub populate straight away. On a **new** building you get a preview, and it is written when you Save Building.',
        'The extractor also pulls the CS prose per Specified System — performance standard, inspection and maintenance procedures, reporting requirements — into `building_ss_instances`.',
        'PDF values win. When the form and the PDF disagree, the merge prefers the PDF, and form hydration is skipped while an extract is pending so your inputs are not wiped.',
        'Always review the result on the building Overview and the SS hub. Extraction is a time-saver, not an authority — the signed PDF remains the legal document.',
      ],
    },
    ko: {
      title: 'Compliance Schedule PDF',
      paragraphs: [
        '건물의 Compliance Schedule을 업로드하면 추출기가 대신 읽어 옵니다. 건물 항목, SS 인스턴스, 유닛, 선택적으로 체크리스트 시드까지 채웁니다.',
        '**기존** 건물에서는 추출 결과가 **자동 적용**되어 상세·SS Units·SS 허브에 즉시 반영됩니다. **새** 건물에서는 미리보기가 표시되고 Save Building 시 저장됩니다.',
        '추출기는 Specified System별 CS 본문(performance standard, 점검·유지보수 절차, 보고 요건)도 `building_ss_instances`로 가져옵니다.',
        'PDF 값이 우선합니다. 폼과 PDF가 다르면 병합 시 PDF를 택하며, 추출이 진행 중일 때는 폼 하이드레이션을 건너뛰어 입력값이 지워지지 않게 합니다.',
        '결과는 항상 건물 Overview와 SS 허브에서 확인하세요. 추출은 시간을 줄여 주는 보조 수단이며, 법적 문서는 서명된 PDF입니다.',
      ],
    },
  },

  'Setting BWOF due date': {
    en: {
      title: 'Setting BWOF due date',
      paragraphs: [
        '**CS Issue Date** is the only date entered for this building. The BWOF due date uses that month and day: this year if it has not passed, otherwise next year.',
        'After a full work cycle completes, the system may still roll the due date forward one year.',
      ],
    },
    ko: {
      title: 'BWOF 만기일 설정',
      paragraphs: [
        '**CS Issue Date**만 입력합니다. BWOF 만기일은 그 월·일을 사용합니다. 아직 안 지났으면 올해, 지났으면 내년입니다.',
        '한 해의 작업 사이클이 끝나면 만기일이 다음 해로 넘어갈 수 있습니다.',
      ],
    },
  },

  'Worker floor-plan markers': {
    en: {
      title: 'Worker floor-plan markers',
      paragraphs: [
        'When **ON**, field workers may **drag unit number markers** on the app floor plan to correct placement when the drawn position does not match the site.',
        'Moves are allowed **only for jobs assigned to that worker** on this building — not for every building in the portfolio.',
        'Every move is logged: previous position, new position, reason, worker, role, and date (audit trail for later review).',
        'When **OFF**, markers stay fixed as set in the office (web Floor plan). Workers can still view the plan and inspect units; they cannot reposition markers.',
        'Turn ON for sites where layout often changes or first-time floor-plan setup needs field correction. Leave OFF when the office plan is the sole source of truth.',
      ],
    },
    ko: {
      title: '작업자 평면도 마커 이동',
      paragraphs: [
        '**ON**이면 현장 작업자가 앱 평면도에서 **유닛 번호 마커를 드래그**해, 도면 위치와 현장이 다를 때 위치를 고칠 수 있습니다.',
        '이동은 이 건물에서 **그 작업자에게 배정된 작업**에만 허용됩니다. 포트폴리오의 모든 건물에서 가능한 것은 아닙니다.',
        '이동할 때마다 이전 위치·새 위치·사유·작업자·역할·날짜가 기록됩니다(이후 검토용 감사 로그).',
        '**OFF**이면 마커는 사무실(웹 Floor plan)에서 잡은 그대로 고정됩니다. 작업자는 평면도와 유닛 검사는 할 수 있지만 마커 위치는 바꿀 수 없습니다.',
        '배치가 자주 바뀌거나 최초 평면도 보정이 현장 도움이 필요할 때 ON. 사무실 도면만 기준으로 할 때는 OFF.',
      ],
    },
  },

  'Default building for workers': {
    en: {
      title: 'Default building for workers',
      paragraphs: [
        'When **ON**, this building pack can be **downloaded to every worker’s app** without a personal building assignment. Useful for demo sites, training, or a shared practice building.',
        'When **OFF** (normal production), workers only receive buildings they are assigned to (or that appear on their jobs). That keeps the app focused on their real workload.',
        '**Master / Admin** accounts already see all buildings in the office tools; this checkbox mainly affects **field worker** app download lists.',
        'Do not enable on every live customer building — prefer assignment-based access. Use sparingly for demo or shared practice sites only.',
      ],
    },
    ko: {
      title: '작업자 기본 다운로드 건물',
      paragraphs: [
        '**ON**이면 개인 건물 배정 없이도 **모든 작업자 앱**에 이 건물 팩을 내려받을 수 있습니다. 데모·교육·공용 연습 건물에 적합합니다.',
        '**OFF**(일반 운영)이면 작업자는 배정된 건물(또는 본인 작업에 나온 건물)만 받습니다. 앱이 실제 업무에 집중되도록 합니다.',
        '**Master / Admin**은 사무실 도구에서 이미 전 건물을 봅니다. 이 체크는 주로 **현장 작업자** 앱 다운로드 목록에 영향을 줍니다.',
        '실제 고객 건물에 전부 켜지 마세요. 배정 기반 접근을 기본으로 하고, 데모·공용 연습용에만 제한적으로 사용하세요.',
      ],
    },
  },

  /* ------------------------------------------------- Queues & progress */

  'Paperwork queues': {
    en: {
      title: 'Paperwork queues',
      paragraphs: [
        'Documents waiting on an office action, grouped by what is blocking them.',
        'Typical states: field work done but the certificate not issued; issued and awaiting filing; superseded by an amendment; or a defect report needing follow-up.',
        'Form 12A can only issue when IMR is recorded and **all units pass**, so an open Fail keeps that SS in this queue rather than in the certificate list.',
        'Open a row to reach Building → Paperwork, where you can issue, file or **Amend** (re-issue and supersede the old documents).',
      ],
    },
    ko: {
      title: '서류 대기 큐',
      paragraphs: [
        '사무실 처리가 필요한 문서를, 무엇이 막고 있는지에 따라 묶어 보여 줍니다.',
        '대표적인 상태: 현장 작업은 끝났으나 증명서 미발행, 발행 후 보관 대기, 변경으로 대체됨, 후속 조치가 필요한 하자 리포트.',
        'Form 12A는 IMR이 기록되고 **전 유닛이 합격**해야 발행할 수 있으므로, Fail이 열려 있으면 그 SS는 증명서 목록이 아니라 이 큐에 남습니다.',
        '행을 열면 Building → Paperwork로 이동해 발행·보관 또는 **Amend**(재발행 및 기존 문서 대체)를 할 수 있습니다.',
      ],
    },
  },

  'Due queue': {
    en: {
      title: 'Due queue',
      paragraphs: [
        'One list for everything that needs attention now: **Over Due** (past the due date) plus **Urgent** (still due within 30 days).',
        'It is the single source of truth. Dashboard cards, this panel, the To-Do popup and Jobs `?overdue=1` / `?urgent=1` all read the same rows, so counts never disagree.',
        'Rows may exist **without a job yet**. Normal flow: due approaches → the system asks the default IQP or partner for a visit date → the pack is issued automatically. Open the building Inspections list rather than Issue, unless you truly must create a pack by hand.',
        'Actions are one dropdown plus Confirm: send email, schedule, or force-move. Force-move to Urgent or Over Due adjusts the due date so the item lands in that bucket.',
        'Select several rows to bulk-schedule, or send an Urgent digest email in one action.',
      ],
    },
    ko: {
      title: '만기 큐',
      paragraphs: [
        '지금 조치가 필요한 항목을 한 목록으로 모읍니다. **Over Due**(만기일 경과) + **Urgent**(30일 내 만기).',
        '유일한 기준 목록입니다. 대시보드 카드, 이 패널, To-Do 팝업, Jobs의 `?overdue=1` / `?urgent=1`이 모두 같은 행을 읽으므로 숫자가 어긋나지 않습니다.',
        '**작업(job)이 아직 없는** 행도 있을 수 있습니다. 정상 흐름은 만기 접근 → 시스템이 기본 IQP나 파트너에게 방문 날짜 요청 → 팩 자동 발행입니다. 반드시 수동 생성해야 하는 경우가 아니면 Issue 대신 건물 Inspections 목록을 여세요.',
        '액션은 드롭다운 하나 + Confirm입니다. 메일 발송, 일정 지정, 강제 이동. Urgent나 Over Due로 강제 이동하면 해당 버킷에 들어가도록 만기일이 조정됩니다.',
        '여러 행을 선택해 일괄 일정 지정하거나, Urgent 다이제스트 메일을 한 번에 보낼 수 있습니다.',
      ],
    },
  },

  'BWOF progress': {
    en: {
      title: 'BWOF progress',
      paragraphs: [
        'How close this building is to closing its annual BWOF cycle, measured across its in-scope Specified Systems.',
        'A system counts as done when it is inspected, passing, and its Form 12A is issued with IMR. Any SS in Repairing keeps the cycle open.',
        'On a **PARTIAL** building only the SS marked *include in inspection* are counted — progress reflects our mandate, not the whole Compliance Schedule.',
        'When every in-scope SS is complete, the council pack can be submitted through **Apply BWOF**; after approval the cycle closes and dates roll forward.',
        'Colours match the rest of the app: Pass = green, Incomplete = yellow, Fail = red.',
      ],
    },
    ko: {
      title: 'BWOF 진행률',
      paragraphs: [
        '이 건물이 연간 BWOF 사이클을 닫기까지 얼마나 남았는지, 검사 범위의 Specified System 기준으로 보여 줍니다.',
        '점검 완료 + 합격 + IMR 포함 Form 12A 발행이 끝난 시스템만 완료로 계산됩니다. Repairing 상태인 SS가 하나라도 있으면 사이클은 열린 상태입니다.',
        '**PARTIAL** 건물에서는 *include in inspection*으로 표시된 SS만 계산합니다. 진행률은 Compliance Schedule 전체가 아니라 우리 위임 범위를 반영합니다.',
        '범위 내 모든 SS가 완료되면 **Apply BWOF**로 시청 제출 묶음을 보낼 수 있습니다. 승인 후 사이클이 닫히고 날짜가 이월됩니다.',
        '색상은 앱 전체와 동일합니다. Pass = 초록, Incomplete = 노랑, Fail = 빨강.',
      ],
    },
  },

  'Service mandates': {
    en: {
      title: 'Service mandates',
      paragraphs: [
        'Hidden when inspection coverage is **Partial**.',
        'When this building is **Full agent** and some Specified Systems are not ones we cover, those SS are listed here with the agent who holds them.',
        'Pick the agent from the registered companies, or use Edit to change the name.',
      ],
    },
    ko: {
      title: '서비스 위임',
      paragraphs: [
        '점검 범위가 **Partial**이면 이 칸은 나오지 않습니다.',
        '건물이 **Full agent**이고 우리가 담당하지 않는 Specified System이 있으면, 그 SS와 그것을 맡은 agent 이름이 여기에 나옵니다.',
        '등록된 회사에서 agent를 고르거나, Edit로 이름을 바꿉니다.',
      ],
    },
  },

  'Full agent': {
    en: {
      title: 'Full agent',
      paragraphs: [
        'A full-agent mandate means we hold the **entire BWOF obligation** for this building — every Specified System on the Compliance Schedule, the council pack and the certificate.',
        'Inspection scope is FULL, so every SS appears on operational pages and every one must pass before the cycle can close.',
        'You do not tick individual systems here; the mandate is the whole schedule. Set the default assignee per SS so each system has someone to ask for a date.',
        'We coordinate in-house IQPs, partner companies for systems we do not cover, and experts for reports the IQP cannot sign.',
        'Choose **SS-only** instead when the owner keeps the overall BWOF and we service selected systems.',
      ],
    },
    ko: {
      title: '전체 대행',
      paragraphs: [
        'Full agent 위임은 이 건물의 **BWOF 의무 전체**를 우리가 보유한다는 뜻입니다. Compliance Schedule의 모든 Specified System, 시청 제출 묶음, 증명서까지 포함합니다.',
        'inspection scope는 FULL입니다. 모든 SS가 운영 화면에 나오고, 사이클을 닫으려면 전부 합격해야 합니다.',
        '여기서는 개별 시스템을 체크하지 않습니다. 위임 대상이 스케줄 전체이기 때문입니다. 각 시스템에 날짜를 요청할 대상이 있도록 SS별 기본 담당자를 설정하세요.',
        '자체 IQP, 우리가 다루지 않는 시스템의 파트너 회사, IQP가 서명할 수 없는 리포트의 전문가를 함께 조율합니다.',
        '건물주가 전체 BWOF를 보유하고 우리가 일부 시스템만 담당한다면 **SS-only**를 선택하세요.',
      ],
    },
  },

  /* --------------------------------------------------- Inspection ops */

  'Set Inspection List': {
    en: {
      title: 'Set Inspection List',
      paragraphs: [
        'Confirm the questions this unit will be inspected against. This is the **only** place per-unit questions are edited.',
        'Pick from the SS default pool or the equipment-type pool. Your selection is saved as a **snapshot** on the unit (`confirmed_checklist_json`), so later template edits do not silently change this unit.',
        'Inspection prefers the snapshot. If nothing is confirmed here, the worker sees an empty checklist — that is the first thing to check when a list comes back blank.',
        'Units of the same equipment subtype share a question set, so confirming one subtype covers its siblings consistently.',
        'Each question can carry long how-to text (**Info**) and guidance photos; those travel with it to the worker app and appear above the pass criteria.',
      ],
    },
    ko: {
      title: '점검 항목 설정',
      paragraphs: [
        '이 유닛을 어떤 문항으로 점검할지 확정합니다. 유닛별 문항을 편집하는 **유일한** 경로입니다.',
        'SS 기본 풀 또는 장비 타입 풀에서 선택합니다. 선택 결과는 유닛에 **스냅샷**(`confirmed_checklist_json`)으로 저장되므로, 이후 템플릿을 수정해도 이 유닛이 조용히 바뀌지 않습니다.',
        '점검은 스냅샷을 우선 사용합니다. 여기서 확정하지 않으면 작업자에게 빈 체크리스트가 보입니다. 목록이 비었을 때 가장 먼저 확인할 지점입니다.',
        '같은 장비 서브타입의 유닛은 문항 세트를 공유하므로, 한 서브타입을 확정하면 동일 유형 유닛에 일관되게 적용됩니다.',
        '문항마다 상세 설명(**Info**)과 안내 사진을 붙일 수 있고, 이 내용은 작업자 앱까지 함께 전달되어 합격 기준 위에 표시됩니다.',
      ],
    },
  },

  'Building inspections': {
    en: {
      title: 'Building inspections',
      paragraphs: [
        'All inspections for this building: one row per Specified System work order, with status, due date, assignee and the admin’s next action.',
        'Statuses run Issued → Scheduled → In progress → Completed, with Failed → Repairing as the branch. The **Admin next** column says what the office should do now, and the row CTAs (Query / Schedule / Start·Finish / Repairing) do it.',
        'Completed rows leave the Open and Over Due views. Use the **Completed (archive)** filter when you need history.',
        'On a PARTIAL building only in-scope Specified Systems appear here.',
        'Use **Issue inspection** only when a due date did not auto-create a work pack — the normal path is the notify cycle asking the IQP for a date.',
      ],
    },
    ko: {
      title: '건물 인스펙션',
      paragraphs: [
        '이 건물의 모든 인스펙션입니다. Specified System 작업 1건이 1행이며, 상태·만기일·담당자와 관리자의 다음 조치를 보여 줍니다.',
        '상태는 Issued → Scheduled → In progress → Completed로 진행되며 Failed → Repairing이 분기입니다. **Admin next** 열이 사무실에서 지금 할 일을 알려 주고, 행의 CTA(Query / Schedule / Start·Finish / Repairing)로 바로 실행합니다.',
        '완료된 행은 Open과 Over Due 화면에서 빠집니다. 이력이 필요하면 **Completed (archive)** 필터를 사용하세요.',
        'PARTIAL 건물에서는 검사 범위에 포함된 Specified System만 표시됩니다.',
        '**Issue inspection**은 만기일이 되어도 작업 팩이 자동 생성되지 않았을 때만 사용하세요. 정상 경로는 통지 사이클이 IQP에게 날짜를 요청하는 것입니다.',
      ],
    },
  },

  'New inspection': {
    en: {
      title: 'New inspection',
      paragraphs: [
        'Create one inspection: building + SS code + due date. That is the whole identity of the record.',
        'This is the **exception path**. Normally the due cycle asks the default IQP or partner for a date and the work pack is issued automatically — create one by hand only when that did not happen.',
        'The assignee should be someone registered for that SS in the IQP Register; only a registered IQP can sign its Form 12A.',
        'Set the due date to the real compliance date, not today. The due date drives Over Due / Urgent buckets, notify lead times and BWOF progress.',
        'Do **not** create a new inspection to record a repair. After a FAIL the same record continues as the repair track.',
      ],
    },
    ko: {
      title: '새 인스펙션',
      paragraphs: [
        '인스펙션 1건을 만듭니다. 건물 + SS 코드 + 만기일이 이 레코드의 전부입니다.',
        '**예외 경로**입니다. 보통은 만기 사이클이 기본 IQP나 파트너에게 날짜를 요청하고 작업 팩이 자동 발행됩니다. 그 과정이 실패했을 때만 직접 생성하세요.',
        '담당자는 IQP 등록부에서 해당 SS에 등록된 사람이어야 합니다. 등록된 IQP만 그 SS의 Form 12A에 서명할 수 있습니다.',
        '만기일은 오늘이 아니라 실제 컴플라이언스 날짜로 넣으세요. 만기일이 Over Due / Urgent 분류, 통지 리드타임, BWOF 진행률을 결정합니다.',
        '수리 기록을 위해 새 인스펙션을 만들지 **마세요**. FAIL 이후에는 같은 레코드가 수리 트랙으로 이어집니다.',
      ],
    },
  },

  'Issue inspection': {
    en: {
      title: 'Issue inspection',
      paragraphs: [
        'Manual issue of a work pack. Use it **only** when a due date did not auto-create one.',
        'The normal path: the due cycle asks the default IQP or partner company for a visit date, and the pack is issued automatically once a date comes back.',
        '**Issue next inspection** creates the following cycle’s pack for an SS; **Issue all missing (n)** fills gaps where scoped systems have no pack at all.',
        'Before issuing manually, check why it is missing — usually no default assignee on the mandate, or the SS is out of scope on a PARTIAL building. Issuing by hand hides that cause without fixing it.',
        'Issued packs appear in the building Inspections list and in the worker app once the pack downloads.',
      ],
    },
    ko: {
      title: '인스펙션 수동 발행',
      paragraphs: [
        '작업 팩을 수동으로 발행합니다. 만기일에 팩이 자동 생성되지 **않았을 때만** 사용하세요.',
        '정상 경로: 만기 사이클이 기본 IQP나 파트너 회사에 방문 날짜를 요청하고, 날짜가 회신되면 팩이 자동 발행됩니다.',
        '**Issue next inspection**은 해당 SS의 다음 사이클 팩을 만들고, **Issue all missing (n)**은 범위 내 시스템 중 팩이 아예 없는 공백을 채웁니다.',
        '수동 발행 전에 왜 없는지 확인하세요. 대개 위임에 기본 담당자가 없거나, PARTIAL 건물에서 그 SS가 범위 밖인 경우입니다. 수동 발행은 원인을 고치지 않고 가릴 뿐입니다.',
        '발행된 팩은 건물 Inspections 목록에 나타나고, 팩이 다운로드되면 작업자 앱에도 표시됩니다.',
      ],
    },
  },

  'Inspection ops story': {
    en: {
      title: 'Inspection ops story',
      paragraphs: [
        'How one inspection actually moves, end to end.',
        'Before due, by frequency: notify the default inspector or partner company → they confirm a date → **Scheduled**. On the day → **In progress**. When results arrive → **Completed**, or Fail → **Repairing**.',
        'Lead times: MONTHLY = 2 weeks before due (visit remind 1 week); QUARTERLY and SEMI-ANNUAL = 1 month before (remind 2 weeks); ANNUAL = 2 months before (remind 1 month).',
        'Steps in order: notify the assignee with a proposed window → they confirm by button reply (no reply → contact them and the admin sets the date) → day of visit → results in → Completed or Failed → Repairing.',
        'Admin-first by design: the office web drives status, and the worker app supplies the field record. Every status has an owner and an **Admin next** action so nothing waits on nobody.',
      ],
    },
    ko: {
      title: '인스펙션 운영 흐름',
      paragraphs: [
        '인스펙션 1건이 처음부터 끝까지 실제로 어떻게 움직이는지 설명합니다.',
        '만기 전, 주기에 따라: 기본 검사자 또는 파트너 회사에 통지 → 날짜 확정 → **Scheduled**. 방문일 → **In progress**. 결과 도착 → **Completed**, 불합격이면 → **Repairing**.',
        '리드타임: MONTHLY = 만기 2주 전(방문 리마인드 1주), QUARTERLY·SEMI-ANNUAL = 1개월 전(리마인드 2주), ANNUAL = 2개월 전(리마인드 1개월).',
        '순서: 담당자에게 제안 구간과 함께 통지 → 버튼 회신으로 날짜 확정(무응답 시 연락 후 관리자가 날짜 지정) → 방문일 → 결과 수신 → Completed 또는 Failed → Repairing.',
        '설계상 관리자 중심입니다. 사무실 웹이 상태를 움직이고 작업자 앱이 현장 기록을 제공합니다. 모든 상태에 담당자와 **Admin next** 조치가 있어 누구도 기다리지 않은 채 방치되지 않습니다.',
      ],
    },
  },

  'Admin controls': {
    en: {
      title: 'Admin controls',
      paragraphs: [
        'The office actions available on this inspection, including the ones that override the normal flow.',
        '**Next step** advances one stage: Pending / Queried / Accepted / Waiting → Confirm → Scheduled; Scheduled → Start work; In progress → Complete (pass); Failed → Start repairing; Repairing → Complete after re-inspect passes.',
        '**Force move** is the override. Booked / In Progress / Completed set the real status, while Urgent and Over Due are due-date buckets — moving there may adjust the due date so the item lands in that queue.',
        'Admin can also record field work directly when the worker cannot: mark In progress, enter results, and complete.',
        'Overrides are logged. Prefer fixing the cause (missing assignee, wrong due date, out-of-scope SS) over repeatedly forcing status.',
      ],
    },
    ko: {
      title: '관리자 제어',
      paragraphs: [
        '이 인스펙션에서 사무실이 수행할 수 있는 조치입니다. 정상 흐름을 우회하는 기능도 포함합니다.',
        '**다음 단계**는 한 단계를 진행시킵니다. Pending / Queried / Accepted / Waiting → Confirm → Scheduled, Scheduled → Start work, In progress → Complete(합격), Failed → Start repairing, Repairing → 재검사 합격 후 Complete.',
        '**Force move**는 강제 이동입니다. Booked / In Progress / Completed는 실제 상태를 바꾸고, Urgent와 Over Due는 만기일 버킷이므로 해당 큐에 들어가도록 만기일이 조정될 수 있습니다.',
        '작업자가 처리할 수 없을 때 관리자가 현장 작업을 직접 기록할 수도 있습니다. In progress 표시, 결과 입력, 완료 처리.',
        '강제 조치는 기록됩니다. 상태를 반복해서 강제하기보다 원인(담당자 누락, 잘못된 만기일, 범위 밖 SS)을 고치는 편이 좋습니다.',
      ],
    },
  },

  'What is an Inspection?': {
    en: {
      title: 'What is an Inspection?',
      paragraphs: [
        'An Inspection is **one Specified System work order on one building** — the scheduled check against the Compliance Schedule. After a Fail it becomes the repair track for that SS.',
        'Identity: building + SS code + due date + status. Nothing else defines it.',
        '**Inspection visit** — check the SS against Form 12 / the unit checklist. **Repair track** — the same record after FAIL: quote, repair, re-inspect until Pass / Complete.',
        'An Inspection is **not** “the whole building BWOF”. The building cycle closes only when all SS inspections are done and the council pack is approved.',
        'Frequency comes from the Compliance Schedule; where the CS is silent, the Unit Standard requirement applies.',
      ],
    },
    ko: {
      title: 'Inspection이란?',
      paragraphs: [
        'Inspection은 **한 건물의 Specified System 1건에 대한 작업 지시**입니다. Compliance Schedule 기준의 정기 점검이며, 불합격 후에는 그 SS의 수리 트랙이 됩니다.',
        '식별 요소: 건물 + SS 코드 + 만기일 + 상태. 그 외에는 없습니다.',
        '**점검 방문** — Form 12 / 유닛 체크리스트로 SS를 확인합니다. **수리 트랙** — FAIL 이후의 같은 레코드로, 견적·수리·재검사를 Pass / Complete까지 진행합니다.',
        'Inspection은 “건물 BWOF 전체”가 **아닙니다**. 건물 사이클은 모든 SS 점검이 끝나고 시청 제출 묶음이 승인될 때 닫힙니다.',
        '주기는 Compliance Schedule에서 옵니다. CS에 명시가 없으면 Unit Standard 요건을 따릅니다.',
      ],
    },
  },

  'Over Due': {
    en: {
      title: 'Over Due',
      paragraphs: [
        'Past the due date — the work is **late**. This is not the same as Urgent.',
        'Urgent = still due within 30 days. Over Due = already past. A past-due item appears only under Over Due, never in both.',
        'Admin: open each row, assign or schedule if needed, then Start → Complete or Fail on the Inspection detail.',
        'Completing clears Over Due (both the badge and the filter). Completed rows stay out of Open and Over Due; use **Completed (archive)** only when you need history.',
        'The lifecycle behind it: before due, notify the default inspector or partner → they confirm a date → Scheduled → In progress → Completed, or Fail → Repairing.',
      ],
    },
    ko: {
      title: '기한 초과',
      paragraphs: [
        '만기일이 지난 상태입니다. 작업이 **늦었습니다**. Urgent와는 다릅니다.',
        'Urgent = 아직 30일 내 만기. Over Due = 이미 경과. 만기가 지난 항목은 Over Due에만 나타나며 양쪽에 동시에 표시되지 않습니다.',
        '관리자: 각 행을 열어 필요하면 배정·일정을 잡고, 인스펙션 상세에서 Start → Complete 또는 Fail을 진행합니다.',
        '완료하면 Over Due가 해제됩니다(배지와 필터 모두). 완료된 행은 Open과 Over Due에서 빠지므로, 이력이 필요할 때만 **Completed (archive)**를 사용하세요.',
        '뒤에 있는 흐름: 만기 전 기본 검사자 또는 파트너에게 통지 → 날짜 확정 → Scheduled → In progress → Completed, 불합격이면 Repairing.',
      ],
    },
  },

  Urgent: {
    en: {
      title: 'Urgent',
      paragraphs: [
        'Still due **within 30 days** — not overdue yet.',
        'Separate from Over Due: past-due items appear only under Over Due, so the two lists never double-count.',
        'Wait for the IQP to confirm a date (the notify cycle asks), or Schedule it here if you must set the date yourself.',
        'Once the work is done, the admin field lifecycle is the same as any other inspection: In progress → Completed, or Fail → Repairing.',
        'Selecting several rows lets you bulk-schedule or send an Urgent digest email in one action.',
      ],
    },
    ko: {
      title: '긴급',
      paragraphs: [
        '아직 **30일 내 만기**입니다. 기한이 지난 것은 아닙니다.',
        'Over Due와 분리되어 있습니다. 만기가 지난 항목은 Over Due에만 나타나므로 두 목록이 중복 집계되지 않습니다.',
        'IQP의 날짜 확정을 기다리세요(통지 사이클이 요청합니다). 직접 날짜를 정해야 하면 여기서 Schedule하세요.',
        '작업이 끝나면 관리자 처리 흐름은 다른 인스펙션과 같습니다. In progress → Completed, 불합격이면 Repairing.',
        '여러 행을 선택해 일괄 일정 지정 또는 Urgent 다이제스트 메일을 한 번에 보낼 수 있습니다.',
      ],
    },
  },

  'Due work (start here)': {
    en: {
      title: 'Due work (start here)',
      paragraphs: [
        'The same list as Dashboard → **Due queue**: Over Due plus Urgent, in one place, so this is the right screen to open first each day.',
        'The clock splits it into **Over Due** and **Due soon**. For due-soon items the system asks the default IQP or company for a visit date (Scheduling).',
        'When a date arrives, the work pack is issued **automatically**. Issue manually only if that pack is genuinely missing.',
        'Rows may have **no pack yet**. Open the building Inspections list rather than Issue, unless you must create a pack by hand.',
        'Bulk actions live here too: select rows to schedule, or send the Urgent email digest.',
      ],
    },
    ko: {
      title: '처리할 작업 (여기서 시작)',
      paragraphs: [
        '대시보드 → **Due queue**와 같은 목록입니다. Over Due와 Urgent를 한곳에 모았으므로 매일 가장 먼저 열기에 적합한 화면입니다.',
        '기한 기준으로 **Over Due**와 **Due soon**으로 나뉩니다. Due soon 항목은 시스템이 기본 IQP 또는 회사에 방문 날짜를 요청합니다(Scheduling).',
        '날짜가 오면 작업 팩이 **자동 발행**됩니다. 그 팩이 정말 없을 때만 수동 발행하세요.',
        '**작업 팩이 아직 없는** 행도 있습니다. 수동 생성이 꼭 필요한 경우가 아니면 Issue 대신 건물 Inspections 목록을 여세요.',
        '일괄 작업도 여기서 합니다. 행을 선택해 일정을 잡거나 Urgent 다이제스트 메일을 보낼 수 있습니다.',
      ],
    },
  },

  /* --------------------------------------------------------- Documents */

  'Company files': {
    en: {
      title: 'Company files',
      paragraphs: [
        'The company drawer: blank forms, letterhead, licences and other files that do not belong to one building. Upload, view, download, send, move and delete.',
        'Registering a company creates `{CompanyName}/document/` plus its category folders, so there is a home for paperwork from day one. The root is configured by `COMPANY_CABINET_PATH` or `DOCUMENTS_PATH`.',
        'Form templates can be seeded into `{Category}/2020-01-01/Templates/` — Form 12A, 11, 11A, S-RaD, B-RaD, Application and Form 12 (BWOF), each in blank, company pre-filled and Auckland guide versions, with company name and address inserted automatically.',
        'Issued copies are not kept here. They go into that building’s Documents as `{BuildingName}/{YYYY}/ss {code}-{12a|srad|brad|defect}-{BuildingName}-{YYYY-MM-DD}`.',
      ],
    },
    ko: {
      title: '회사 파일',
      paragraphs: [
        '회사 서랍입니다. 빈 양식, 레터헤드, 면허처럼 어느 한 건물에 속하지 않는 파일을 둡니다. 업로드·조회·다운로드·전송·이동·삭제가 가능합니다.',
        '회사를 등록하면 `{회사명}/document/`와 카테고리 폴더가 생성되어 처음부터 서류 보관 위치가 준비됩니다. 루트 경로는 `COMPANY_CABINET_PATH` 또는 `DOCUMENTS_PATH`로 설정합니다.',
        '양식 템플릿을 `{카테고리}/2020-01-01/Templates/`에 시드할 수 있습니다. Form 12A, 11, 11A, S-RaD, B-RaD, Application, Form 12(BWOF)를 각각 빈 양식·회사 정보 기입본·오클랜드 가이드본으로 제공하며 회사명과 주소가 자동 삽입됩니다.',
        '발행 사본은 여기에 두지 않습니다. 그 건물의 Documents에 `{건물명}/{연도}/ss 코드-12a|srad|brad|defect-건물명-발행일`로 들어갑니다.',
      ],
    },
  },

  'Building documents': {
    en: {
      title: 'Building documents',
      paragraphs: [
        'Documents filed against this building: Compliance Schedule, certificates, inspection reports, defect reports and partner paperwork.',
        'Storage is configured in **Edit Building → Documents**, which maps the storage root, syncs the company default folders, and lets you add or delete custom folders.',
        'Certificate copies use `{BuildingName}/{YYYY}/ss {code}-{type}-{BuildingName}-{date}` so folders read by building then year.',
        'Uploaded Compliance Schedules and partner reports can be run through extraction so their data lands in the system instead of being retyped. The original file always stays as the evidence copy.',
      ],
    },
    ko: {
      title: '건물 문서',
      paragraphs: [
        '이 건물에 귀속된 문서입니다. Compliance Schedule, 증명서, 점검 리포트, 하자 리포트, 파트너 서류를 보관합니다.',
        '저장 설정은 **Edit Building → Documents**에서 합니다. 저장 루트를 지정하고, 회사 기본 폴더를 동기화하며, 커스텀 폴더를 추가·삭제할 수 있습니다.',
        '증명서 사본은 `{건물명}/{연도}/ss 코드-유형-건물명-날짜` 형식이라 폴더가 건물→연도 순으로 읽힙니다.',
        '업로드한 Compliance Schedule과 파트너 리포트는 추출을 거쳐 데이터를 시스템에 넣을 수 있어 재입력이 필요 없습니다. 원본 파일은 항상 근거 사본으로 남습니다.',
      ],
    },
  },

  /* ------------------------------------------------------ Admin account */

  'Add Admin': {
    en: {
      title: 'Add Admin',
      paragraphs: [
        'Create an office admin login. Login accepts **username or email**, and the same credentials work on the worker app when the account allows it.',
        'Give **one login per person**. Shared accounts break the audit trail — schedules, issued documents and amendments are recorded against whoever was signed in.',
        'New accounts are better created in **Staff**, which handles every login (admins and workers) in one profile form with access groups and per-person rights.',
        'Office roles are ADMIN / MANAGER / MASTER. MASTER keeps full authority including Compliance Schedule extract and company settings.',
      ],
    },
    ko: {
      title: '관리자 추가',
      paragraphs: [
        '사무실 관리자 로그인을 만듭니다. 로그인은 **아이디 또는 이메일**을 모두 받고, 계정이 허용하면 같은 정보로 작업자 앱에도 로그인합니다.',
        '**한 사람당 하나의 로그인**을 부여하세요. 계정을 공유하면 감사 이력이 무너집니다. 일정 지정, 증명서 발행, 변경 작업은 그 시점에 로그인한 사람 기준으로 기록됩니다.',
        '새 계정은 **Staff**에서 만드는 것이 좋습니다. 관리자와 작업자를 하나의 프로필 폼에서 접근 그룹과 개인별 권한까지 함께 관리합니다.',
        '사무실 역할은 ADMIN / MANAGER / MASTER입니다. MASTER는 Compliance Schedule 추출과 회사 설정을 포함한 전체 권한을 가집니다.',
      ],
    },
  },

  'Edit Admin': {
    en: {
      title: 'Edit Admin',
      paragraphs: [
        'Update this admin’s profile, role and login. Login accepts **username or email**.',
        'Keep **one login per person**. Do not hand an existing account to a new staff member — past actions in the audit trail would then read as theirs.',
        '**Deactivate rather than delete** when someone leaves. History (who scheduled, issued or amended what) stays readable and stays linked to their record.',
        'Changing an access group only takes effect after that person **re-logs in**. A worker account moved to access group Admin signs in as an admin from the next login.',
      ],
    },
    ko: {
      title: '관리자 수정',
      paragraphs: [
        '이 관리자의 프로필, 역할, 로그인 정보를 수정합니다. 로그인은 **아이디 또는 이메일**을 모두 받습니다.',
        '**한 사람당 하나의 로그인**을 유지하세요. 기존 계정을 새 직원에게 넘기지 마세요. 그러면 과거 감사 이력이 그 사람의 행위처럼 남습니다.',
        '퇴사 시에는 **삭제 대신 비활성화**하세요. 누가 일정을 잡고 발행·수정했는지의 이력이 계정에 연결된 상태로 남습니다.',
        '접근 그룹 변경은 해당 사용자가 **다시 로그인**한 뒤에 적용됩니다. 접근 그룹이 Admin으로 바뀐 작업자 계정은 다음 로그인부터 관리자로 로그인합니다.',
      ],
    },
  },

  /* ------------------------------------------------ Staff access groups */

  Admin: {
    en: {
      title: 'Admin',
      paragraphs: [
        'Office / master — full web and app portfolio.',
        '**Who:** Office staff and MASTER accounts that run the admin site (admin.cbsl.co.nz) and may also use the worker app.',
        '**What they can do:** Manage buildings, schedule, inspections, repairing, Registered Company, Staff, documents and settings. Full portfolio visibility.',
        '**How to use:** Create as an *Admin* account type, or assign access group Admin. Set a username / password for web login. MASTER workers also belong here for setup rights.',
        '**Tip:** Prefer one login per person so audit logs show who changed schedules and jobs.',
      ],
    },
    ko: {
      title: '관리자 (Admin)',
      paragraphs: [
        '사무실 / 마스터 — 웹과 앱 포트폴리오 전체 접근.',
        '**대상:** 관리자 사이트(admin.cbsl.co.nz)를 운영하는 사무실 직원과 MASTER 계정. 작업자 앱도 함께 사용할 수 있습니다.',
        '**가능한 작업:** 건물, 일정, 인스펙션, 수리, 등록 업체, 직원, 문서, 설정 관리. 포트폴리오 전체를 볼 수 있습니다.',
        '**사용 방법:** *Admin* 계정 유형으로 만들거나 접근 그룹을 Admin으로 지정합니다. 웹 로그인용 아이디·비밀번호를 설정하세요. 설정 권한이 필요한 MASTER 작업자도 여기에 속합니다.',
        '**팁:** 한 사람당 하나의 로그인을 쓰세요. 그래야 누가 일정과 작업을 변경했는지 감사 로그에 남습니다.',
      ],
    },
  },

  'Worker (internal)': {
    en: {
      title: 'Worker (internal)',
      paragraphs: [
        'Cornerstone field IQP — app inspect.',
        '**Who:** Cornerstone field IQPs who inspect specified systems on site with the phone app.',
        '**What they can do:** Sign into the worker app with the same username / password, receive assigned jobs, record Pass / Fail / N/A, and sync when online. Web access is limited unless they are also MASTER.',
        '**How to use:** Add staff as *Worker*, set access group to Worker (internal), add the licence / IQP number if known, then open **IQP** on the row to attach SS grades and certificates.',
        '**Tip:** Keep departed field staff as Inactive (soft-delete) so history stays linked to their jobs.',
      ],
    },
    ko: {
      title: '작업자 (내부)',
      paragraphs: [
        'Cornerstone 소속 현장 IQP — 앱으로 점검 수행.',
        '**대상:** 휴대폰 앱으로 현장에서 Specified System을 점검하는 Cornerstone 현장 IQP.',
        '**가능한 작업:** 같은 아이디·비밀번호로 작업자 앱에 로그인해 배정된 작업을 받고 Pass / Fail / N/A를 기록하며, 온라인일 때 동기화합니다. MASTER가 아니면 웹 접근은 제한됩니다.',
        '**사용 방법:** 직원을 *Worker*로 추가하고 접근 그룹을 Worker (internal)로 설정합니다. 라이선스·IQP 번호를 알면 입력한 뒤, 행의 **IQP**를 열어 SS 등급과 증명서를 연결하세요.',
        '**팁:** 퇴사한 현장 직원은 삭제하지 말고 Inactive(소프트 삭제)로 두세요. 그래야 작업 이력이 계정에 연결된 상태로 남습니다.',
      ],
    },
  },

  'Worker (external)': {
    en: {
      title: 'Worker (external)',
      paragraphs: [
        'External contractor / partner IQP.',
        '**Who:** Partner or contractor IQPs who inspect some SS on our buildings (often linked via Form 12A / Registered Company), not full Cornerstone employees.',
        '**What they can do:** The same app login pattern as internal workers when you issue them an account. Access is scoped by how you assign jobs and building rights.',
        '**How to use:** Prefer registering the company under Registered Company first. Create a worker account here only when they need an app login. Set the group to Worker (external) so they stay separate from internal IQPs.',
        '**Tip:** Council register contacts who do not need an app login can stay in Registered Company / Registered IQP without a Staff account.',
      ],
    },
    ko: {
      title: '작업자 (외부)',
      paragraphs: [
        '외부 협력업체 / 파트너 IQP.',
        '**대상:** 우리 건물의 일부 SS를 점검하는 파트너·협력업체 IQP입니다(주로 Form 12A / 등록 업체로 연결). Cornerstone 정식 직원은 아닙니다.',
        '**가능한 작업:** 계정을 발급하면 내부 작업자와 같은 방식으로 앱에 로그인합니다. 접근 범위는 작업 배정과 건물 권한 설정으로 제한됩니다.',
        '**사용 방법:** 먼저 Registered Company에 업체를 등록하는 것이 좋습니다. 앱 로그인이 필요할 때만 여기서 작업자 계정을 만드세요. 내부 IQP와 구분되도록 그룹을 Worker (external)로 설정합니다.',
        '**팁:** 앱 로그인이 필요 없는 시청 등록부 연락처는 Staff 계정 없이 Registered Company / Registered IQP에만 두어도 됩니다.',
      ],
    },
  },

  Guest: {
    en: {
      title: 'Guest',
      paragraphs: [
        'Limited / temporary access.',
        '**Who:** Temporary or restricted logins — trial, auditor, short-term helper.',
        '**What they can do:** Minimal access — treat as read / limited until you raise their group. Do not use Guest for day-to-day field IQPs or office admins.',
        '**How to use:** Create the account, set access group Guest, give a short-lived password, then either promote it to the correct group or deactivate / delete when finished.',
        '**Tip:** Prefer Inactive over leaving old Guest accounts active.',
      ],
    },
    ko: {
      title: '게스트',
      paragraphs: [
        '제한적 / 임시 접근.',
        '**대상:** 임시 또는 제한된 로그인 — 시험 사용, 감사인, 단기 지원 인력.',
        '**가능한 작업:** 최소 권한입니다. 그룹을 올려 주기 전까지는 읽기·제한 수준으로 취급하세요. 상시 현장 IQP나 사무실 관리자에게 Guest를 쓰지 마세요.',
        '**사용 방법:** 계정을 만들고 접근 그룹을 Guest로 설정한 뒤 단기용 비밀번호를 부여합니다. 이후 적절한 그룹으로 올리거나, 종료 시 비활성화·삭제하세요.',
        '**팁:** 오래된 Guest 계정을 활성 상태로 두지 말고 Inactive로 바꾸세요.',
      ],
    },
  },

  /* ------------------------------------------- Inspection status filters */

  Issued: {
    en: {
      title: 'Issued',
      paragraphs: [
        'Work pack issued (automatically after a visit date, or by Issue manually). The date may still be open.',
        '**Who:** System or Admin.',
        '**Admin next:** Wait for the IQP date if Scheduling is running, or Schedule / Ask if it is still on us.',
        'Do not issue the same pack again. A duplicate pack splits the evidence for one Specified System across two records.',
      ],
    },
    ko: {
      title: '발행됨',
      paragraphs: [
        '작업 팩이 발행된 상태입니다(방문 날짜 확정 후 자동, 또는 수동 발행). 날짜는 아직 미정일 수 있습니다.',
        '**주체:** 시스템 또는 관리자.',
        '**관리자 다음 조치:** Scheduling이 진행 중이면 IQP의 날짜를 기다리고, 아직 우리 쪽 일이면 Schedule 또는 문의를 진행하세요.',
        '같은 팩을 다시 발행하지 마세요. 팩이 중복되면 하나의 Specified System에 대한 근거가 두 레코드로 쪼개집니다.',
      ],
    },
  },

  'Auction / Query': {
    en: {
      title: 'Auction / Query',
      paragraphs: [
        '**Queried:** Awaiting reply — an offer was sent by app or email and the counterpart has not answered.',
        '**Auction:** Open claim — the first qualified worker can take the inspection.',
        '**Admin next:** Wait for an accept, assign manually, or open the inspection and Start / Complete on their behalf.',
        'Both are **legacy** assignment statuses. They remain in the database and filters, but the daily flow is notify → confirm date → Scheduled.',
      ],
    },
    ko: {
      title: '입찰 / 문의',
      paragraphs: [
        '**Queried:** 회신 대기 — 앱 또는 이메일로 제안을 보냈지만 상대가 답하지 않은 상태입니다.',
        '**Auction:** 공개 선점 — 자격을 갖춘 작업자 중 먼저 잡는 사람이 인스펙션을 가져갑니다.',
        '**관리자 다음 조치:** 수락을 기다리거나, 직접 배정하거나, 인스펙션을 열어 대신 Start / Complete를 진행하세요.',
        '두 상태 모두 **legacy** 배정 방식입니다. DB와 필터에는 남아 있지만, 일상 흐름은 통지 → 날짜 확정 → Scheduled입니다.',
      ],
    },
  },

  'Awaiting reply': {
    en: {
      title: 'Awaiting reply',
      paragraphs: [
        'Awaiting reply — an offer was sent by app or email; the counterpart has not answered.',
        '**Who:** Admin → Worker / company.',
        '**Admin next:** Nudge, or wait. Do not Issue again unless the pack is actually missing.',
        'If no reply keeps coming back, set the visit date manually on the inspection detail rather than leaving the row to age into Over Due.',
      ],
    },
    ko: {
      title: '회신 대기',
      paragraphs: [
        '회신 대기 상태입니다. 앱 또는 이메일로 제안을 보냈으나 상대가 답하지 않았습니다.',
        '**주체:** 관리자 → 작업자 / 업체.',
        '**관리자 다음 조치:** 재촉하거나 기다리세요. 팩이 실제로 없는 경우가 아니면 다시 발행하지 마세요.',
        '계속 회신이 없으면 인스펙션 상세에서 방문 날짜를 직접 지정하세요. 그대로 두면 행이 Over Due로 넘어갑니다.',
      ],
    },
  },

  Auction: {
    en: {
      title: 'Auction',
      paragraphs: [
        'Open claim — the first qualified worker can take the inspection.',
        '**Who:** Admin → Worker.',
        '**Admin next:** Monitor claims, or assign / complete as admin if needed.',
        'A **legacy** status: kept in the database and filters but de-emphasised in the daily admin flow, which assigns the default IQP or partner instead.',
      ],
    },
    ko: {
      title: '공개 선점',
      paragraphs: [
        '공개 선점 상태입니다. 자격을 갖춘 작업자 중 먼저 잡는 사람이 인스펙션을 가져갑니다.',
        '**주체:** 관리자 → 작업자.',
        '**관리자 다음 조치:** 선점 현황을 지켜보거나, 필요하면 관리자가 직접 배정·완료 처리하세요.',
        '**legacy** 상태입니다. DB와 필터에는 남아 있지만 일상 관리 흐름에서는 강조하지 않습니다. 기본 IQP 또는 파트너를 배정하는 방식을 사용합니다.',
      ],
    },
  },

  Accepted: {
    en: {
      title: 'Accepted',
      paragraphs: [
        'A worker is responsible for this inspection; the date is not fixed yet.',
        '**Who:** Worker (accept) / Admin (assign).',
        '**Admin next:** Schedule the visit (booking).',
        'A **legacy** assignment status. In the current flow a confirmed date moves the item straight to Scheduled.',
      ],
    },
    ko: {
      title: '수락됨',
      paragraphs: [
        '작업자가 이 인스펙션을 담당하기로 한 상태입니다. 날짜는 아직 확정되지 않았습니다.',
        '**주체:** 작업자(수락) / 관리자(배정).',
        '**관리자 다음 조치:** 방문 일정을 잡으세요(예약).',
        '**legacy** 배정 상태입니다. 현재 흐름에서는 날짜가 확정되면 곧바로 Scheduled로 넘어갑니다.',
      ],
    },
  },

  Scheduled: {
    en: {
      title: 'Scheduled',
      paragraphs: [
        'The visit date and time are set on the calendar.',
        '**Who:** Admin.',
        '**Admin next:** Start work when on site (or after an offline visit), then Complete or Fail.',
        'Scheduled rows show on the Schedule calendar. If the date slips, change it here rather than letting the item fall into Over Due unexplained.',
      ],
    },
    ko: {
      title: '일정 확정',
      paragraphs: [
        '방문 날짜와 시간이 캘린더에 지정된 상태입니다.',
        '**주체:** 관리자.',
        '**관리자 다음 조치:** 현장에 도착하면(또는 오프라인 방문 후) Start work를 누른 뒤 Complete 또는 Fail을 처리하세요.',
        '확정된 행은 Schedule 캘린더에 표시됩니다. 날짜가 밀리면 여기서 변경하세요. 그대로 두면 이유 없이 Over Due로 넘어갑니다.',
      ],
    },
  },

  Notified: {
    en: {
      title: 'Notified',
      paragraphs: [
        'Lead time reached: the system created the inspection and emailed the internal IQP and the related company together.',
        'Listed facts: who was emailed (IQP / company), the send count, the last sent time, and the propose-date status.',
        'Appears on the Schedule calendar as a **Notify chip** on the compliance due date.',
        '**Admin next:** Wait for the email confirmation, or set the visit date manually on the inspection detail.',
        'No reply means daily chase emails until a date is proposed, or until you stop or override it.',
      ],
    },
    ko: {
      title: '통지됨',
      paragraphs: [
        '리드타임에 도달해 시스템이 인스펙션을 생성하고 내부 IQP와 관련 업체에 함께 메일을 보낸 상태입니다.',
        '표시되는 정보: 메일 수신자(IQP / 업체), 발송 횟수, 마지막 발송 시각, 날짜 제안 상태.',
        'Schedule 캘린더에서 컴플라이언스 만기일에 **Notify 칩**으로 표시됩니다.',
        '**관리자 다음 조치:** 이메일 확인을 기다리거나, 인스펙션 상세에서 방문 날짜를 직접 지정하세요.',
        '회신이 없으면 날짜가 제안될 때까지(또는 중지·강제 처리할 때까지) 매일 독촉 메일이 발송됩니다.',
      ],
    },
  },

  Waiting: {
    en: {
      title: 'Waiting',
      paragraphs: [
        'The worker asked to wait, or the item is on a soft hold.',
        '**Who:** Worker / Admin.',
        '**Admin next:** Follow up, or reassign / schedule.',
        'Waiting does not pause the due date. The item still ages toward Urgent and Over Due, so do not use it as a parking space.',
      ],
    },
    ko: {
      title: '대기',
      paragraphs: [
        '작업자가 대기를 요청했거나, 항목이 임시 보류된 상태입니다.',
        '**주체:** 작업자 / 관리자.',
        '**관리자 다음 조치:** 후속 연락을 하거나 재배정·일정 지정을 진행하세요.',
        'Waiting은 만기일을 멈추지 않습니다. 항목은 계속 Urgent와 Over Due로 진행되므로 보류용으로 사용하지 마세요.',
      ],
    },
  },

  'In Progress': {
    en: {
      title: 'In Progress',
      paragraphs: [
        'Inspection or repair work is underway.',
        '**Who:** Worker app (later) / Admin (now).',
        '**Admin next:** Complete (pass), or Mark fail from the Inspection detail after offline sheets arrive.',
        'Any Fail sends the record to the defect form and then Repairing — the same record continues as the repair track until re-inspection passes.',
      ],
    },
    ko: {
      title: '진행 중',
      paragraphs: [
        '점검 또는 수리 작업이 진행 중인 상태입니다.',
        '**주체:** 작업자 앱(향후) / 관리자(현재).',
        '**관리자 다음 조치:** Complete(합격)로 처리하거나, 오프라인 시트를 받은 뒤 인스펙션 상세에서 불합격으로 표시하세요.',
        'Fail이 발생하면 하자 양식을 거쳐 Repairing으로 넘어갑니다. 같은 레코드가 재검사 합격까지 수리 트랙으로 이어집니다.',
      ],
    },
  },

  Failed: {
    en: {
      title: 'Failed',
      paragraphs: [
        'The inspection failed — defects were found and the repair path starts.',
        '**Who:** Inspector outcome.',
        '**Admin next:** Send reports, request a quote, then move to Repairing.',
        'Form 12A needs IMR plus **all units passing**, so an open Fail blocks the certificate for that Specified System and keeps the building’s BWOF cycle open.',
      ],
    },
    ko: {
      title: '불합격',
      paragraphs: [
        '인스펙션이 불합격한 상태입니다. 하자가 발견되어 수리 절차가 시작됩니다.',
        '**주체:** 검사자 판정.',
        '**관리자 다음 조치:** 리포트를 발송하고 견적을 요청한 뒤 Repairing으로 이동하세요.',
        'Form 12A는 IMR과 **전 유닛 합격**을 요구하므로, 미해결 Fail이 있으면 그 Specified System의 증명서가 막히고 건물의 BWOF 사이클이 열린 상태로 남습니다.',
      ],
    },
  },

  'After paperwork': {
    en: {
      title: 'After paperwork',
      paragraphs: [
        'The visit is done and the paperwork upload has been requested or received.',
        '**Who:** IQP / company → Admin.',
        '**Admin next:** Review the uploads, then Confirm paperwork done → Completed.',
        'This is where external partner work lands: the visit happened offline, and the office is waiting on (or checking) the returned certificate and report.',
      ],
    },
    ko: {
      title: '서류 대기',
      paragraphs: [
        '방문은 끝났고 서류 업로드를 요청했거나 이미 받은 상태입니다.',
        '**주체:** IQP / 업체 → 관리자.',
        '**관리자 다음 조치:** 업로드 내용을 검토한 뒤 서류 완료를 확인하면 Completed가 됩니다.',
        '외부 파트너 작업이 도달하는 지점입니다. 방문은 오프라인으로 진행되었고, 사무실은 회신된 증명서와 리포트를 기다리거나 확인하는 중입니다.',
      ],
    },
  },

  'Completed (archive)': {
    en: {
      title: 'Completed (archive)',
      paragraphs: [
        'This SS inspection is finished for this cycle.',
        '**Who:** Worker app or Admin.',
        '**Admin next:** No inspection action — check the building Close (council pack) once all SS are done.',
        'Completed rows stay out of the Open and Over Due views on purpose. Use this archive filter only when you need history.',
      ],
    },
    ko: {
      title: '완료 (보관)',
      paragraphs: [
        '이 SS 인스펙션이 해당 사이클에서 완료된 상태입니다.',
        '**주체:** 작업자 앱 또는 관리자.',
        '**관리자 다음 조치:** 인스펙션 관련 조치는 없습니다. 모든 SS가 끝나면 건물의 Close(시청 제출 묶음)를 확인하세요.',
        '완료된 행은 의도적으로 Open과 Over Due 화면에서 빠집니다. 이력이 필요할 때만 이 보관 필터를 사용하세요.',
      ],
    },
  },

  Rejected: {
    en: {
      title: 'Rejected',
      paragraphs: [
        'The worker declined the offer.',
        '**Who:** Worker.',
        '**Admin next:** Query again, or assign another worker.',
        'A **legacy** assignment status. Check the IQP Register before reassigning — only someone registered for that SS can sign its Form 12A.',
      ],
    },
    ko: {
      title: '거절됨',
      paragraphs: [
        '작업자가 제안을 거절한 상태입니다.',
        '**주체:** 작업자.',
        '**관리자 다음 조치:** 다시 문의하거나 다른 작업자를 배정하세요.',
        '**legacy** 배정 상태입니다. 재배정 전에 IQP 등록부를 확인하세요. 해당 SS에 등록된 사람만 그 SS의 Form 12A에 서명할 수 있습니다.',
      ],
    },
  },
};

/**
 * Extra keys that point at the same entry.
 * Some source files carry mojibake em-dashes (`??`) in the InfoButton title
 * (`Empty checklist ?? what to check`, `IQP site inspection ??field evidence`);
 * every spelling must resolve to the same help copy.
 */
const ALIASES = {
  'Empty checklist ?? what to check': 'Empty checklist — what to check',
  'Empty checklist -- what to check': 'Empty checklist — what to check',
  'IQP site inspection ??field evidence': 'IQP site inspection — field evidence',
  'IQP site inspection ?? field evidence': 'IQP site inspection — field evidence',
  'IQP site inspection -- field evidence': 'IQP site inspection — field evidence',
};

/** Public catalog: base entries plus aliases resolved to the same objects. */
export const INFO_CATALOG = (() => {
  const out = { ...CATALOG };
  Object.entries(ALIASES).forEach(([alias, target]) => {
    const entry = CATALOG[target];
    if (entry) out[alias] = entry;
  });
  return out;
})();

/** Loose key: drop punctuation/dash/mojibake differences and casing. */
function normalizeKey(key) {
  return String(key == null ? '' : key)
    .toLowerCase()
    .replace(/[^\p{L}\p{N}.]+/gu, ' ')
    .trim();
}

const NORMALIZED = (() => {
  const index = {};
  Object.keys(INFO_CATALOG).forEach((key) => {
    const norm = normalizeKey(key);
    if (norm && !index[norm]) index[norm] = INFO_CATALOG[key];
  });
  return index;
})();

function pickLang(locale) {
  return String(locale || 'en')
    .toLowerCase()
    .startsWith('ko')
    ? 'ko'
    : 'en';
}

/**
 * Resolve localized Info copy for a title / infoKey.
 * Falls back to English when the locale has no translation, and returns
 * null for unknown keys so callers can render their own English children.
 *
 * @param {string} key Info title or explicit infoKey
 * @param {string} [locale] 'ko' | 'en' (anything else resolves to English)
 * @returns {{ title: string, paragraphs: string[] } | null}
 */
export function lookupInfo(key, locale = 'en') {
  if (!key) return null;
  const raw = String(key);
  const entry = INFO_CATALOG[raw] || INFO_CATALOG[raw.trim()] || NORMALIZED[normalizeKey(raw)];
  if (!entry) return null;

  const picked = entry[pickLang(locale)] || entry.en;
  if (!picked) return null;

  return {
    title: picked.title || raw,
    paragraphs: Array.isArray(picked.paragraphs) ? picked.paragraphs : [],
  };
}

export default INFO_CATALOG;
